import React__default, { forwardRef, useEffect, useRef, useState, useCallback, useLayoutEffect, useMemo, useContext, createContext, useImperativeHandle } from 'react';
import { c as classNames, E as ErrorBoundary, F as FixedSizeList } from './vendor-ce770e52.js';
import { createUseStyles, useTheme } from 'react-jss';
import { defineMessages, t, T, L10NContext } from '@sm/intl';
import { visuallyHidden, uniqueId } from '@wds/utils';
import { sanitizeString } from '@sm/utils';
import ReactDOM from 'react-dom';
import { c as contrastColor } from './theme-22bba55b.js';
const useStyles$C = createUseStyles(theme => ({
  wdsIcons: ({
    color,
    size
  }) => {
    var _a, _b, _c;

    const fontSize = size ? {
      fontSize: theme.iconSize[size]
    } : {
      fontSize: 'inherit'
    };
    const chosenColor = // @ts-expect-error Fallback styles are acceptable
    ((_a = theme.palette) === null || _a === void 0 ? void 0 : _a.text[color]) || ((_c = (_b = theme.palette) === null || _b === void 0 ? void 0 : _b[color]) === null || _c === void 0 ? void 0 : _c.main) || 'currentColor';
    return { ...fontSize,
      width: '1em',
      height: '1em',
      display: 'inline-block',
      verticalAlign: 'middle',
      textAlign: 'center',
      fill: chosenColor
    };
  }
})); // https://reactjs.org/docs/higher-order-components.html#convention-wrap-the-display-name-for-easy-debugging

const getDisplayName = Component => Component.displayName || Component.name || 'Component';

function withIcon(Component) {
  function WithIcon({
    color,
    title,
    size,
    ...rest
  }) {
    const classes = useStyles$C({
      color,
      size
    });
    return React__default.createElement("svg", {
      className: classes.wdsIcons,
      viewBox: "0 0 16 16",
      preserveAspectRatio: "xMidYMid",
      "aria-label": title,
      role: title ? 'img' : 'presentation',
      ...rest
    }, React__default.createElement(Component, null));
  }

  WithIcon.displayName = `WithIcon(${getDisplayName(Component)})`;

  WithIcon.getOriginalComponent = () => Component;

  return WithIcon;
}

const useSurveyIconStyles = createUseStyles({
  svgContainer: {
    display: 'inline-block',
    width: '1em',
    height: '1em'
  }
});
let instance = 0;

function withSvgIcon(Component, displayName) {
  function SvgIcon({
    id,
    'aria-labelledby': ariaLabelledBy,
    title,
    viewBox = '0 0 16 16',
    className,
    ...props
  }) {
    instance += 1;
    const styles = useSurveyIconStyles();
    const hasTitle = !!title && !ariaLabelledBy;
    const iconTitleId = id ? `${id}-icon-title` : `icon-title-${instance}`;
    return React__default.createElement("svg", {
      className: classNames(styles.svgContainer, className),
      viewBox: viewBox,
      preserveAspectRatio: "xMidYMid",
      "aria-hidden": hasTitle ? undefined : true,
      "aria-labelledby": hasTitle ? iconTitleId : ariaLabelledBy,
      role: hasTitle ? 'img' : undefined,
      focusable: "false",
      ...props,
      "data-testid": displayName
    }, hasTitle && React__default.createElement("title", {
      id: iconTitleId
    }, title), React__default.createElement(Component, null));
  }

  SvgIcon.displayName = displayName;
  return SvgIcon;
}

function DocumentXIconPath() {
  return React__default.createElement("path", {
    d: "m5.94.005 8.012.012C14.53.018 15 .487 15 1.067V14.95c0 .58-.47 1.05-1.05 1.05H2.05C1.47 16 1 15.53 1 14.95V4.94L5.94.004zM7 1.505V4.95c0 .541-.41.987-.936 1.044L5.95 6H2.5v8.5h11V1.514L7 1.505zM6.557 7.53 8 8.973 9.443 7.53a.765.765 0 0 1 1.083 1.082l-1.444 1.443 1.444 1.444a.765.765 0 0 1-1.083 1.082L8 11.138 6.557 12.58A.765.765 0 1 1 5.474 11.5l1.444-1.444-1.444-1.443A.765.765 0 1 1 6.557 7.53zM5.5 2.563 3.561 4.5H5.5V2.562z"
  });
}

var IconDocumentX = withIcon(DocumentXIconPath);
const useStyles$B = createUseStyles(theme => {
  var _a, _b;

  return {
    fallbackComponent: {
      fontFamily: (_b = (_a = theme.questionBody) === null || _a === void 0 ? void 0 : _a.fontFamily) !== null && _b !== void 0 ? _b : {},
      color: theme.questionColor,
      border: `1px solid ${theme.questionColor}`,
      borderRadius: '5px',
      width: '100%',
      height: '100%',
      minHeight: '220px',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      '& svg': {
        fill: theme.questionColor
      }
    },
    errorMessage: {
      marginLeft: `8px` // equivalent to wrench theme.spacing[2]

    }
  };
});
const COPY$3 = defineMessages({
  ERROR: {
    id: 'FallbackComponent.ERROR',
    defaultMessage: 'Error loading question',
    description: '[Type: label][Vis: high] - error message indicating a question could not be loaded'
  }
});

function FallbackComponent() {
  const {
    fallbackComponent,
    errorMessage
  } = useStyles$B();
  return React__default.createElement("div", {
    className: fallbackComponent
  }, React__default.createElement(IconDocumentX, null), React__default.createElement("p", {
    className: errorMessage
  }, t(COPY$3.ERROR)));
}

const withErrorBoundary = Component => {
  const displayName = Component.displayName || Component.name || 'Component';

  function ComponentWithErrorBoundary(props) {
    return React__default.createElement(ErrorBoundary, {
      FallbackComponent: FallbackComponent
    }, React__default.createElement(Component, { ...props
    }));
  }

  ComponentWithErrorBoundary.displayName = `withErrorBoundary(${displayName})`;
  return ComponentWithErrorBoundary;
};

const useStyles$A = createUseStyles(theme => {
  var _a;

  const breakpointMedium = `@media only screen and (max-width: ${theme.breakpoints.md.max})`;
  const breakpointXsMin = `@media only screen and (min-width: ${theme.breakpoints.xs.min})`;
  return {
    inputField: {
      fontFamily: (_a = theme.questionBody.fontFamily) !== null && _a !== void 0 ? _a : {},
      fontSize: '18px',
      [breakpointMedium]: {
        fontSize: '16px'
      },
      fontWeight: theme.fontWeight.medium,
      lineHeight: '1.5em',
      height: ({
        autoHeight
      }) => autoHeight ? {} : '50px',
      maxWidth: '100%',
      width: '100%',
      [breakpointXsMin]: {
        // extra 12px to account for the padding
        width: ({
          size
        }) => `calc(${size}ch + 12px)`
      },
      padding: '6px',
      border: `1px solid ${theme.answerColor}`,
      borderRadius: '0px',
      backgroundColor: '#fff',
      color: '#000',
      transition: 'all 0.1s linear',
      '&:focus, &:hover': {
        outline: `2px solid ${theme.primaryAccentColor}`
      },
      '&:read-only:not(:disabled)': {
        borderColor: 'transparent',
        backgroundColor: theme.input.bgColor,
        color: theme.isDark ? '#fff' : '#000',
        opacity: 0.5
      },
      '&:disabled': {
        opacity: 0.4
      }
    }
  };
});

function TextInput({
  className,
  autoHeight = false,
  size = 50,
  required = false,
  disabled = false,
  readOnly = false,
  onChange: handleChange,
  ...props
}, forwardedRef) {
  const {
    inputField
  } = useStyles$A({
    autoHeight,
    size
  });
  return React__default.createElement("input", {
    type: "text",
    className: classNames(inputField, className),
    disabled: disabled,
    "aria-disabled": disabled,
    required: required,
    "aria-required": required,
    readOnly: readOnly,
    "aria-readonly": readOnly,
    spellCheck: true,
    onChange: handleChange,
    ...props,
    ref: forwardedRef
  });
}

var TextInput$1 = forwardRef(TextInput);
/**
 * Maximum input characters is 20,000
 * Resolves: RAWR-1186
 */

const TEXT_INPUT_MAX_CHARS = 20000;

const createFieldId = questionId => `question-field-${questionId}`;

const createLegendId = questionId => `question-title-legend-${questionId}`;

const createErrorRowId = questionId => `error-row-${questionId}`;

const createErrorId = questionId => `${questionId}-validation-message`;

const useStyles$z = createUseStyles(theme => {
  var _a;

  return {
    button: {
      color: theme.nextButton.color,
      backgroundColor: theme.nextButton.backgroundColor,
      fontFamily: (_a = theme.questionBody.fontFamily) !== null && _a !== void 0 ? _a : {},
      fontSize: 15,
      border: [1, 'solid', 'transparent'],
      borderRadius: 2,
      padding: [1, 6],
      maxWidth: '100%',
      minWidth: 60,
      minHeight: 40,
      cursor: 'pointer',
      overflowWrap: 'break-word',
      transition: `all .2s linear`,
      '&:focus, &:hover': {
        outlineOffset: 5,
        outline: `1px solid ${theme.answerColor}`,
        // Safari seems to have an outline bug, may not render without boxshadow
        boxShadow: '0 0 4px transparent'
      }
    }
  };
});
const COPY$2 = defineMessages({
  OK_LABEL: {
    id: 'Button.OK_LABEL',
    defaultMessage: 'OK',
    description: '[Type: button][Vis: high] - question ok label'
  }
}); // This might need to be a prop later once confirmed

const MAX_LENGTH = 20;

function Button({
  text = t(COPY$2.OK_LABEL),
  type = 'button',
  className,
  ...props
}) {
  const {
    button
  } = useStyles$z();
  const slicedText = text.length <= MAX_LENGTH ? text : text.slice(0, MAX_LENGTH);
  return (// eslint-disable-next-line react/button-has-type
    React__default.createElement("button", {
      className: classNames(button, className),
      type: type,
      ...props
    }, slicedText)
  );
}
/** Referentially stable empty function
 *
 * e.g. as default or placeholder */
// eslint-disable-next-line @typescript-eslint/no-empty-function


const EmptyFn = () => {};
/* eslint-disable no-param-reassign, no-plusplus */

/**
 * @name EasingFunctions- Useful easing equations
 * @comment only considering the t value for the range [0, 1] => [0, 1]
 */


const easingFunctions = {
  /** @comment linear - no easing, no acceleration */
  linear: t => t,

  /** @comment easeInQuad - accelerating from zero velocity */
  easeInQuad: t => t * t,

  /** @comment easeOutQuad - decelerating to zero velocity */
  easeOutQuad: t => t * (2 - t),

  /** @comment easeInOutQuad - acceleration until halfway, then deceleration */
  easeInOutQuad: t => t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t,

  /** @comment easeInCubic - accelerating from zero velocity */
  easeInCubic: t => t * t * t,

  /** @comment easeOutCubic - decelerating to zero velocity */
  easeOutCubic: t => --t * t * t + 1,

  /** @comment easeInOutCubic - acceleration until halfway, then deceleration */
  easeInOutCubic: t => t < 0.5 ? 4 * t * t * t : (t - 1) * (2 * t - 2) * (2 * t - 2) + 1,

  /** @comment easeInQuart - accelerating from zero velocity */
  easeInQuart: t => t * t * t * t,

  /** @comment easeOutQuart - decelerating to zero velocity */
  easeOutQuart: t => 1 - --t * t * t * t,

  /** @comment easeInOutQuart - acceleration until halfway, then deceleration */
  easeInOutQuart: t => t < 0.5 ? 8 * t * t * t * t : 1 - 8 * --t * t * t * t,

  /** @comment easeInQuint - accelerating from zero velocity */
  easeInQuint: t => t * t * t * t * t,

  /** @comment easeOutQuint - decelerating to zero velocity */
  easeOutQuint: t => 1 + --t * t * t * t * t,

  /** @comment easeInOutQuint - acceleration until halfway, then deceleration */
  easeInOutQuint: t => t < 0.5 ? 16 * t * t * t * t * t : 1 + 16 * --t * t * t * t * t
};
/**
 * Given a start/end point of a scroll and time elapsed, calculate the scroll position we should be at
 */

function getValue(start, end, elapsed, duration, easingType = 'linear') {
  if (elapsed > duration) {
    return end;
  }

  const easing = typeof easingType === 'string' ? easingFunctions[easingType] : easingType;
  return start + (end - start) * easing(elapsed / duration);
}
/**
 * Smoothly animate between two values
 */


function animate({
  fromValue,
  toValue,
  onStart,
  onUpdate,
  onComplete,
  duration = 600,
  delay = 0,
  easingType = 'easeOutQuart'
}) {
  const startTime = performance.now() + delay;

  const tick = () => {
    const elapsed = performance.now() - startTime;
    const time = getValue(fromValue, toValue, elapsed, duration, easingType);
    const callback = elapsed <= duration ? tick : onComplete !== null && onComplete !== void 0 ? onComplete : EmptyFn;

    const updateAnimation = () => {
      onUpdate(time, callback);
    };

    window.requestAnimationFrame(updateAnimation);
  };

  onStart === null || onStart === void 0 ? void 0 : onStart();

  if (delay > 0) {
    setTimeout(() => {
      tick();
    }, delay);
  } else {
    tick();
  }
}

const useStyles$y = createUseStyles({
  visuallyHidden: visuallyHidden
});

function VisuallyHidden({
  children,
  element: Element
}) {
  const {
    visuallyHidden
  } = useStyles$y();
  return React__default.createElement(Element, {
    className: visuallyHidden
  }, children);
}

const useStyles$x = createUseStyles({
  paddingBox: ({
    padding,
    width
  }) => {
    return {
      paddingTop: (padding === null || padding === void 0 ? void 0 : padding.top) || 0,
      paddingBottom: (padding === null || padding === void 0 ? void 0 : padding.bottom) || 0,
      paddingLeft: (padding === null || padding === void 0 ? void 0 : padding.left) || 0,
      paddingRight: (padding === null || padding === void 0 ? void 0 : padding.right) || 0,
      width: width ? `${width.width}${width.format === 'PERCENT' ? '%' : 'px'}` : '100%',
      maxWidth: '100%'
    };
  }
});

function QuestionSpacing({
  padding,
  width,
  children,
  ...props
}) {
  const {
    paddingBox
  } = useStyles$x({
    padding,
    width
  });
  return React__default.createElement("div", {
    className: paddingBox,
    "data-testid": "QuestionSpacing__paddingBox",
    ...props
  }, children);
}
/**
 * Hook that calls the provided callback function when the ref element is blurred,
 * only if the next focus is outside the ref element
 *
 * @example
 * const ref = useRef<HTMLDivElement>(null);
 * useOnFocusLeave(ref, ()=> onLeaveQuestion());
 * <div rf={ref}><input /></div>
 */


function useOnFocusLeave(ref, focusCallback) {
  useEffect(() => {
    const ele = ref === null || ref === void 0 ? void 0 : ref.current;

    function handleFocus(e) {
      if (!(ele === null || ele === void 0 ? void 0 : ele.contains(e.relatedTarget))) {
        focusCallback(ref);
      }
    }

    ele === null || ele === void 0 ? void 0 : ele.addEventListener('focusout', handleFocus);
    return () => {
      ele === null || ele === void 0 ? void 0 : ele.removeEventListener('focusout', handleFocus);
    };
  }, [ref, focusCallback]);
}

const useStyles$w = createUseStyles({
  footerRow: ({
    hasFooterPadding
  }) => ({
    paddingTop: hasFooterPadding ? 20 : 0
  }),
  containerStyles: {
    position: 'relative',
    transition: 'opacity 500ms ease-out'
  },
  errorRow: {
    overflow: 'hidden'
  },
  formReset: {
    border: 0,
    minWidth: 0
  },
  headerRow: {
    marginBottom: 24,
    width: '100%'
  }
});
const COPY$1 = defineMessages({
  QUESTION_TITLE: {
    id: 'QuestionFieldLayoutTemplate.QUESTION_TITLE',
    defaultMessage: 'Question Title',
    description: '[Type: Label][Vis: low] - question landmark for screen readers only'
  }
});

function QuestionFieldLayoutTemplate({
  footer,
  hasFooterPadding,
  children,
  clickShield,
  error,
  id,
  padding,
  width = {
    width: 100,
    format: 'PERCENT'
  },
  title,
  onSubmit,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onLeave = () => {},
  ...props
}) {
  const {
    footerRow,
    containerStyles,
    formReset,
    errorRow,
    headerRow
  } = useStyles$w({
    hasFooterPadding,
    clickShield
  });
  const containerRef = useRef(null);
  const errorRef = useRef(null);
  /** Keep track of last error to enable error-hiding animation */

  const [lastShownError, setLastShownError] = useState(error);
  const handleKeydown = useCallback(event => {
    if (event.key === 'Enter') {
      onSubmit === null || onSubmit === void 0 ? void 0 : onSubmit();
    }
  }, [onSubmit]);
  /**
   * resolves double height animate issue with RAWR-1240
   */

  const isAnimating = useRef(false);
  useLayoutEffect(() => {
    const el = errorRef.current;

    if (!el || isAnimating.current) {
      return;
    }

    const hasError = !!error;
    const initialHeight = el.offsetHeight;
    el.style.height = 'auto'; // remove height to measure full size

    const {
      offsetHeight
    } = el;
    const fromValue = hasError ? 0 : offsetHeight;
    const toValue = hasError ? offsetHeight : 0; // don't animate if value is already correct (e.g. at initial load)

    if (initialHeight === toValue) {
      el.style.height = `${initialHeight}px`;
      return;
    }

    el.style.height = `${initialHeight}px`;
    animate({
      fromValue,
      toValue,
      duration: 300,
      delay: 200,
      onUpdate: (height, next) => {
        el.style.height = `${height}px`;
        next();
      },
      onStart: () => {
        isAnimating.current = true;
      },
      onComplete: () => {
        setLastShownError(error);
        isAnimating.current = false;
      }
    });
  }, [error]);
  const fieldsetRef = useRef(null);
  useOnFocusLeave(fieldsetRef, onLeave);
  useEffect(() => {
    const {
      current: container
    } = containerRef;
    container === null || container === void 0 ? void 0 : container.addEventListener('keydown', handleKeydown);
    return () => {
      return container === null || container === void 0 ? void 0 : container.removeEventListener('keydown', handleKeydown);
    };
  }, [containerRef, handleKeydown]);
  /**
   * This id is queried in RespWeb to calculate the offset for scroll-to-error animations in classic mode
   * ref: https://code.corp.surveymonkey.com/webplatform/smweb/pull/10725
   */

  let errorRowId;
  /** used in RespWeb for OQAATView */

  let fieldId;
  /** used in RespWeb for OQAATView */

  let questionTitleId;

  if (id) {
    errorRowId = createErrorRowId(id);
    fieldId = createFieldId(id);
    questionTitleId = createLegendId(id);
  }

  return React__default.createElement("div", {
    ref: containerRef,
    id: fieldId,
    ...props,
    className: containerStyles
  }, React__default.createElement(VisuallyHidden, {
    element: "div"
  }, React__default.createElement(T, {
    desc: COPY$1.QUESTION_TITLE
  })), React__default.createElement(QuestionSpacing, {
    padding: padding,
    width: width
  }, React__default.createElement("div", {
    ref: errorRef,
    id: errorRowId,
    className: errorRow
  }, error || lastShownError), React__default.createElement("fieldset", {
    ref: fieldsetRef,
    className: formReset
  }, React__default.createElement("legend", {
    id: questionTitleId,
    className: headerRow,
    tabIndex: -1
  }, title), children), footer && React__default.createElement("div", {
    className: footerRow
  }, footer)));
}
/**
 * Calculate the max number of children each column can be filled in the layout
 * @param columns number of columns
 * @param total total child count
 * @description Based on the current implementation in production (as of Jan 20, 2021),
 * - if columns = 1, the max number of children per column = total (render all children vertically)
 * - if columns > 1, the max number of children per column = (total / columns) + 1
 */


const calculateMaxNumOfChildrenPerColumn = (columns, total) => {
  if (columns === 1) {
    return total;
  }

  const childrenPerColumn = total / columns;

  if (childrenPerColumn % 1 === 0) {
    return childrenPerColumn;
  }

  return Math.floor(childrenPerColumn) + 1;
};
/**
 * Divide the children array into slices for rendering
 * @param childrenArr - children array
 * @param maxCellsPerCol - maximum number of children per slice
 * @param totalColumns - number of columns
 * @example
 * childrenArr = [option1, option2, option3, option4, option5, option6, option7]
 * childrenPerSlice = 2
 * totalColumns = 3
 * output: [[option1, option2, option3], [option4, option5], [option6, option7]]
 */


const sliceChildren = (childrenArr, maxCellsPerCol, totalColumns) => {
  let index = 0;
  const result = [];
  const partialColumnsCount = totalColumns * maxCellsPerCol - childrenArr.length; // Calculate the number of fullColumns, to conditionally push slice with the correct # of cells

  let fullColumnsCount = totalColumns - partialColumnsCount;

  while (index < childrenArr.length) {
    if (fullColumnsCount > 0) {
      result.push(childrenArr.slice(index, index + maxCellsPerCol));
      index += maxCellsPerCol;
    } else {
      result.push(childrenArr.slice(index, index + maxCellsPerCol - 1));
      index += maxCellsPerCol - 1;
    }

    fullColumnsCount -= 1;
  }

  return result;
};

const useStyles$v = createUseStyles(theme => ({
  containerVertical: {
    display: 'initial'
  },
  containerHorizontal: {
    display: 'block'
  },

  /** shared between horizontal and vertical columns */
  column: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start'
  },
  columnHorizontal: {},

  /** Horizontal Column that adjust width to content width */
  columnHorizontalAutoAdjust: {
    width: 'auto'
  },
  [`@media (min-width: ${theme.breakpoints.xs.min})`]: {
    containerVertical: {
      display: 'flex'
    },
    containerHorizontal: {
      display: 'flex',
      flexWrap: 'wrap'
    }
  },
  [`@media only screen and (min-width: ${theme.breakpoints.lg.min})`]: {
    columnHorizontal: {
      width: '19%'
    }
  },
  [`@media (min-width: ${theme.breakpoints.sm.min}) and (max-width: ${theme.breakpoints.md.max})`]: {
    columnHorizontal: {
      width: '24%'
    }
  },
  [`@media only screen and (max-width: ${theme.breakpoints.xxs.max})`]: {
    columnHorizontal: {
      width: '100%',
      display: 'block'
    },
    columnHorizontalAutoAdjust: {
      width: '100%',
      display: 'block'
    }
  },
  answerLayoutCell: ({
    gridCellMargin = '0'
  }) => ({
    margin: gridCellMargin,
    flex: [0, 0, 'auto'],
    wordBreak: 'break-all'
  }),
  otherLayoutCell: ({
    otherCellMargin = '0'
  }) => ({
    margin: otherCellMargin
  })
}));

function QuestionAnswerLayoutTemplate({
  children,
  columns = 1,
  other,
  noneOfTheAbove,
  adjustToContent = false,
  gridCellMargin,
  otherCellMargin
}) {
  const {
    containerVertical,
    containerHorizontal,
    column,
    columnHorizontal,
    columnHorizontalAutoAdjust,
    answerLayoutCell,
    otherLayoutCell
  } = useStyles$v({
    columns,
    adjustToContent,
    gridCellMargin,
    otherCellMargin
  });
  const isHorizontal = columns === 'horizontal';
  /* Based on the columns config, divide the question answers
    (children of this component) into column groups for rendering.
       The reason we need to do this is when there are multiple columns,
    the answers are listed vertically instead of horizontally.
    For example:
    If there are 3 answers and 2 columns,
    Then the ordering will be:
    Answer 1    Answer 3
    Answer 2
       NOT:
    Answer 1    Answer 2
    Answer 3
    * though `horizontal` uses this
    
  */

  const columnGroups = useMemo(() => {
    const totalChildren = React__default.Children.count(children);
    const totalColumns = columns === 'horizontal' ? totalChildren : columns;
    const childrenArray = React__default.Children.toArray(children);

    if (!other && !!noneOfTheAbove && columns !== 'horizontal') {
      childrenArray.push(noneOfTheAbove);
    }

    const answersPerColumn = columns === 'horizontal' ? 1 : calculateMaxNumOfChildrenPerColumn(totalColumns, totalChildren + (!other && !!noneOfTheAbove ? 1 : 0));
    return sliceChildren(childrenArray, answersPerColumn, totalColumns);
  }, [children, columns, noneOfTheAbove, other]);
  const columnClassNames = isHorizontal ? [column, adjustToContent ? columnHorizontalAutoAdjust : columnHorizontal] : column;
  return React__default.createElement(React__default.Fragment, null, React__default.createElement("div", {
    className: isHorizontal ? containerHorizontal : containerVertical
  }, columnGroups.map((col, columnIndex) => React__default.createElement("div", {
    key: `col-${columnIndex + 1}`,
    className: classNames(columnClassNames),
    "data-testid": "answer-layout-column"
  }, col.map((cell, cellIndex) => React__default.createElement("div", {
    key: `cell-${cellIndex + 1}`,
    "data-testid": "answer-layout-cell",
    className: answerLayoutCell
  }, cell))))), other && React__default.createElement("div", {
    className: otherLayoutCell
  }, other), (other && noneOfTheAbove || columns === 'horizontal') && React__default.createElement("div", {
    className: otherLayoutCell,
    "data-testid": "Other-NOTA"
  }, noneOfTheAbove));
}

function WarningIcon() {
  return React__default.createElement("g", {
    className: "warning-icon"
  }, React__default.createElement("path", {
    className: "background",
    transform: "translate(0 .49999)",
    d: "M8.49642,0.635797c.1494.086885.27371.210979.36079.360173L15.8621,12.9978c.2782.4766.1169,1.0883-.3602,1.3662-.1529.0891-.3267.136-.5037.136L1,14.5c-.552285,0-1-.4473-1-.999c0-.1766.046846-.35.135766-.5026L7.1291,0.996589c.27785-.476838.89002-.63837,1.36732-.360792Z",
    clipRule: "evenodd",
    fill: "currentColor",
    fillRule: "evenodd",
    stroke: "none"
  }), React__default.createElement("path", {
    className: "foreground",
    transform: "translate(.000815 .49999)",
    d: "M8,11.2546c-.3797,0-.69349.2794-.74315.6419L7.25,11.9973v.26l.00685.1008C7.30651,12.7206,7.6203,13,8,13s.69349-.2794.74315-.6419L8.75,12.2573v-.26l-.00685-.1008C8.69349,11.534,8.3797,11.2546,8,11.2546ZM8,4c-.3797,0-.69349.2794-.74315.6419L7.25,4.74268v4.54393l.00685.10078c.04966.3625.36345.64191.74315.64191s.69349-.27941.74315-.64191L8.75,9.28661v-4.54393L8.74315,4.6419C8.69349,4.2794,8.3797,4,8,4Z",
    clipRule: "evenodd",
    fill: "#fff",
    fillRule: "evenodd",
    stroke: "none"
  }));
}

var WarningIcon$1 = withSvgIcon(WarningIcon, 'WarningIcon');
const useStyles$u = createUseStyles(theme => {
  var _a;

  return {
    icon: {
      color: '#F05B24',
      fontSize: theme.fontSize.body,
      alignSelf: 'flex-start',
      marginTop: 2,
      flexShrink: 0
    },
    validationMessage: {
      fontFamily: theme.fontFamily,
      lineHeight: 1.5,
      color: '#333e48',
      fontWeight: 400,
      fontSize: 16,
      padding: theme.isDark ? 8 : [4, 0],
      backgroundColor: theme.isDark ? 'rgba(255, 255, 255, 0.9)' : '',
      borderRadius: 2,
      display: 'inline-flex',
      gap: 5,
      alignItems: 'center',
      marginBottom: 8,
      [`@media only screen and (max-width: ${(_a = theme.breakpoints) === null || _a === void 0 ? void 0 : _a.xxs.max})`]: {
        width: '100%',
        padding: theme.isDark ? 4 : [4, 0]
      }
    }
  };
});

function ValidationMessage({
  children,
  id,
  tag: Wrapper = 'span',
  ...props
}) {
  const {
    icon,
    validationMessage
  } = useStyles$u();
  return React__default.createElement(Wrapper, {
    className: validationMessage,
    "aria-live": "polite",
    id: id,
    ...props
  }, React__default.createElement(WarningIcon$1, {
    className: icon
  }), React__default.createElement("span", {
    translate: "no"
  }, children));
}

const useStyles$t = createUseStyles(theme => {
  var _a;

  const breakpointMedium = `@media only screen and (max-width: ${theme.breakpoints.md.max})`;
  const breakpointXsMin = `@media only screen and (min-width: ${theme.breakpoints.xs.min})`;
  return {
    inputArea: {
      fontFamily: (_a = theme.questionBody.fontFamily) !== null && _a !== void 0 ? _a : {},
      fontWeight: theme.fontWeight.medium,
      fontSize: '18px',
      [breakpointMedium]: {
        fontSize: '16px'
      },
      lineHeight: '1.15em',
      padding: '6px 60px 6px 6px',
      maxWidth: '100%',
      [breakpointXsMin]: {
        // extra 66px to account for the padding
        width: ({
          cols
        }) => `calc(${cols}ch + 66px)`
      },
      width: '100%',
      border: `1px solid ${theme.answerColor}`,
      borderRadius: '0px',
      backgroundColor: '#fff',
      color: '#000',
      transition: 'all 0.1s linear',
      verticalAlign: 'top',
      textSizeAdjust: 'auto',
      '&:focus, &:hover': {
        outline: `2px solid ${theme.primaryAccentColor}`
      },
      '&:read-only:not(:disabled)': {
        borderColor: 'transparent',
        backgroundColor: theme.input.bgColor,
        color: theme.isDark ? '#fff' : '#000',
        opacity: 0.5
      },
      '&:disabled': {
        opacity: 0.4
      }
    }
  };
});

function TextArea({
  className,
  disabled = false,
  required = false,
  readOnly = false,
  cols = 50,
  ...props
}, forwardedRef) {
  const {
    inputArea
  } = useStyles$t({
    cols
  });
  return React__default.createElement("textarea", {
    className: classNames(inputArea, className),
    disabled: disabled,
    "aria-disabled": disabled,
    required: required,
    "aria-required": required,
    readOnly: readOnly,
    "aria-readonly": readOnly,
    spellCheck: true,
    ref: forwardedRef,
    ...props
  });
}

var TextArea$1 = forwardRef(TextArea);
const useStyles$s = createUseStyles(theme => {
  return {
    commentLabelText: { ...theme.questionBody,
      marginBottom: 5,
      lineHeight: 1.25
    },
    commentLabel: {
      display: 'block'
    }
  };
});

function CommentChoice({
  label,
  lineCount = 3,
  charCount = 50,
  onChange,
  className,
  ...props
}) {
  const handleChange = e => {
    onChange === null || onChange === void 0 ? void 0 : onChange(e);
  };

  const {
    commentLabel,
    commentLabelText
  } = useStyles$s();
  const multipleTextLines = lineCount > 1;
  return React__default.createElement("div", {
    className: className
  }, React__default.createElement("label", {
    htmlFor: props.id,
    className: commentLabel,
    tabIndex: -1
  }, React__default.createElement("div", {
    className: commentLabelText
  }, label), multipleTextLines ? React__default.createElement(TextArea$1, {
    onChange: handleChange,
    rows: lineCount,
    cols: charCount,
    ...props
  }) : React__default.createElement(TextInput$1, {
    autoHeight: true,
    onChange: handleChange,
    size: charCount,
    ...props
  })));
}

const useStyles$r = createUseStyles(theme => {
  var _a, _b;

  return {
    tooltip: {
      'span[data-tooltip] &': {
        display: 'none'
      },
      [`@media (min-width: ${(_b = (_a = theme.breakpoints) === null || _a === void 0 ? void 0 : _a.md.min) !== null && _b !== void 0 ? _b : '769px'})`]: {
        'span[data-tooltip]:hover &, span[data-tooltip]:focus &': {
          display: 'inline-block'
        }
      },
      position: 'absolute',
      left: ({
        center
      }) => center,
      bottom: 20,
      width: 275,
      transform: 'translate(-50%, -5px)',
      textAlign: 'center',
      zIndex: 12,
      fontSize: 13,
      '&:after': {
        position: 'absolute',
        bottom: -4,
        left: '50%',
        width: 10,
        height: 10,
        backgroundColor: 'white',
        content: '""',
        marginLeft: -5,
        transform: 'rotate(45deg)',
        boxShadow: [[3, 5, 5, 'rgb(0 0 0 / 0.07)']],
        zIndex: 13
      }
    },
    tooltipBody: {
      display: 'inline-block',
      maxWidth: 275,
      color: 'black',
      fontWeight: 300,
      fontFamily: theme.fontFamily,
      fontStyle: 'normal',
      boxShadow: [[0, 2, 10, 0, 'rgb(0 0 0 / 0.14)']],
      background: 'white',
      padding: [[12, 16]],
      lineHeight: 1.4,
      borderRadius: 2,
      textAlign: 'left'
    }
  };
});
const defaultSelectors = ['a[href]', 'button', 'textarea', 'input:not([type="hidden"])', 'select', 'details', '[tabindex]:not([tabindex="-1"])'];

const useFocusTrap = ({
  selectors = defaultSelectors
} = {}) => {
  const focusRef = React__default.useRef(null);
  const [focusableEls, setEls] = React__default.useState([]);
  React__default.useEffect(() => {
    var _a;

    setEls(Array.from((_a = focusRef === null || focusRef === void 0 ? void 0 : focusRef.current) === null || _a === void 0 ? void 0 : _a.querySelectorAll(selectors.join(','))));
  }, [focusRef, selectors]);

  function handleFocus(e) {
    if (focusRef.current) {
      const focusableElements = focusableEls;
      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === firstElement) {
          lastElement.focus();
          e.preventDefault();
        }
      } else if (document.activeElement === lastElement) {
        firstElement.focus();
        e.preventDefault();
      }
    }
  }

  React__default.useEffect(() => {
    const handleKeyDown = e => {
      if (e.key === 'Tab') {
        handleFocus(e);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  });
  return focusRef;
};

const useStyles$q = createUseStyles(theme => {
  var _a;

  return {
    modalOverlay: {
      zIndex: 999,
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.6)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'flex-end'
    },
    modal: {
      position: 'relative',
      width: '85vw',
      minHeight: '20vw',
      marginBottom: 20,
      background: 'white',
      borderRadius: 2
    },
    closeBtn: {
      position: 'absolute',
      cursor: 'pointer',
      background: 'transparent',
      right: 0,
      border: 0,
      padding: 8,
      marginTop: 0,
      color: 'black',
      zIndex: 105
    },
    tooltipText: {
      color: ((_a = theme.questionBody) === null || _a === void 0 ? void 0 : _a.color) || '#333E48',
      fontFamily: theme.fontFamily,
      fontSize: 13,
      fontWeight: 400,
      lineHeight: '1.4',
      padding: [28, 12, 16]
    }
  };
});
const COPY = defineMessages({
  CLOSE_MODAL: {
    id: 'Modal.CloseModal',
    defaultMessage: 'Close tooltip',
    description: '[Type: label][Vis: Med] - close button text'
  }
});

function Modal({
  tooltip,
  closeModal
}) {
  const {
    modalOverlay,
    closeBtn,
    modal,
    tooltipText
  } = useStyles$q();
  const closeRef = React__default.useRef(null);
  const tooltipId = uniqueId('tooltip-modal');
  const focusRef = useFocusTrap();

  const handleClick = e => {
    // click on button or overlay should close modal
    e.stopPropagation();

    if (e.currentTarget === e.target) {
      closeModal();
    }
  };

  React__default.useEffect(() => {
    // listen for ESC to close modal
    const handleKeydown = e => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        closeModal();
      }
    };

    document.addEventListener('keydown', handleKeydown);
    return () => {
      document.removeEventListener('keydown', handleKeydown);
    };
  }, [closeModal]);
  React__default.useEffect(() => {
    var _a;

    (_a = closeRef.current) === null || _a === void 0 ? void 0 : _a.focus();
  }, []);
  return React__default.createElement("div", {
    className: modalOverlay,
    onClick: handleClick,
    "aria-hidden": "true"
  }, React__default.createElement("div", {
    ref: focusRef,
    className: modal,
    role: "dialog",
    "aria-modal": "true",
    "aria-describedby": tooltipId
  }, React__default.createElement("button", {
    type: "button",
    className: closeBtn,
    ref: closeRef,
    onClick: handleClick,
    "aria-label": t(COPY.CLOSE_MODAL)
  }, "\u00D7"), React__default.createElement("p", {
    id: tooltipId,
    className: tooltipText
  }, tooltip)));
}
/** Displays a tooltip on hover for large screens and a modal on click for small screens */


const Tooltip = ({
  container,
  className,
  id = ''
}) => {
  var _a;
  /** container center position to display tooltip */


  const center = container.offsetWidth / 2;
  const text = (_a = container.getAttribute('data-tooltip')) !== null && _a !== void 0 ? _a : '';
  const theme = useTheme();
  const {
    tooltip,
    tooltipBody
  } = useStyles$r({
    center
  });
  const [showModal, setShowModal] = React__default.useState(false);
  const [mode, setMode] = React__default.useState('TOOLTIP');

  const closeModal = () => {
    setShowModal(false);
    container.focus();
  };

  React__default.useEffect(() => {
    container.setAttribute('role', 'tooltip'); // make tooltip anchor focusable

    if (!container.hasAttribute('tabIndex')) {
      container.setAttribute('tabIndex', '0');
    } // opens modal if matches small screen breakpoint


    const openModal = () => {
      var _a, _b;

      const mql = window.matchMedia(`(max-width: ${(_b = (_a = theme === null || theme === void 0 ? void 0 : theme.breakpoints) === null || _a === void 0 ? void 0 : _a.md.min) !== null && _b !== void 0 ? _b : '769px'})`);

      if (mql.matches) {
        setMode('MODAL');
        setShowModal(true);
      } else {
        setMode('TOOLTIP');
      }
    };

    const handleClick = () => {
      openModal();
    };

    const handleKeydown = e => {
      if (e.code === 'Enter' || e.code === 'Space') {
        e.preventDefault();
        e.stopPropagation();
        openModal();
      }
    }; // listen for click, ENTER or SPACE to open modal


    container.addEventListener('click', handleClick);
    container.addEventListener('keydown', handleKeydown);
    return () => {
      container.removeEventListener('click', handleClick);
      container.removeEventListener('keydown', handleKeydown);
    };
  }, [container, theme]);
  const modalEl = showModal ? React__default.createElement(Modal, {
    tooltip: text,
    closeModal: closeModal
  }) : null;
  const tooltipEl = React__default.createElement("div", {
    className: classNames(tooltip, className)
  }, React__default.createElement("span", {
    id: id,
    className: tooltipBody,
    "aria-hidden": "true"
  }, text));
  const child = mode === 'MODAL' ? modalEl : tooltipEl;
  const containerEl = mode === 'MODAL' ? document.body : container;
  return ReactDOM.createPortal(child, containerEl);
};

const listSpacing = {
  margin: [16, 0],
  padding: [0, 0, 0, 40]
};
const mediaReset = {
  border: 0,
  maxWidth: '100%'
};
const useStyles$p = createUseStyles({
  richTextContent: {
    // Typography
    '& a': {
      // Links are not themed by default. Users must select a link color in the editor
      color: 'blue',
      textDecoration: 'underline',
      '&:hover': {
        cursor: 'pointer'
      },
      '&:focus': {
        outline: 'currentColor auto 1px'
      }
    },
    '& ol': { ...listSpacing,
      listStyleType: 'decimal'
    },
    '& ul': { ...listSpacing,
      listStyle: 'disc outside none',
      '& ul li': {
        listStyle: 'circle'
      }
    },
    '& p': {
      margin: [13, 0]
    },
    '& b': {
      fontWeight: 'bold'
    },
    '& strong': {
      fontWeight: 'bold'
    },
    '& em': {
      fontStyle: 'italic'
    },
    '& i': {
      fontStyle: 'italic'
    },
    '& big': {
      fontSize: 'larger'
    },
    '& small': {
      fontSize: 'smaller'
    },
    '& sup': {
      fontSize: '75%',
      lineHeight: 0,
      position: 'relative',
      top: '-0.5em',
      verticalAlign: 'baseline'
    },
    // Media embeds
    '& iframe': mediaReset,
    '& img': { ...mediaReset,
      height: 'auto',
      '-ms-interpolation-mode': 'bicubic'
    },
    '& span[data-tooltip]': {
      position: 'relative',
      display: 'inline',
      borderBottom: [[1, 'dashed']],
      textDecoration: 'inherit',
      cursor: 'pointer'
    }
  }
}); // Support iframe video embeds and their matching attributes

const sanitizeOptions = {
  ADD_TAGS: ['iframe'],
  ADD_ATTR: ['target', 'allow', 'allowfullscreen', 'frameborder', 'sandbox', 'scrolling']
};

function RichText({
  id: elementId,
  element: Element = 'div',
  text,
  className,
  ...containerProps
}) {
  const {
    richTextContent
  } = useStyles$p();
  const [tooltipNodes, setTooltipNodes] = React__default.useState([]);
  const tooltipRef = React__default.useRef(null);
  React__default.useEffect(() => {
    if (tooltipRef === null || tooltipRef === void 0 ? void 0 : tooltipRef.current) {
      const el = tooltipRef === null || tooltipRef === void 0 ? void 0 : tooltipRef.current;
      setTooltipNodes(Array.from(el.querySelectorAll('[data-tooltip]')));
    }
  }, []);
  return React__default.createElement(React__default.Fragment, null, React__default.createElement(Element, {
    id: elementId,
    className: classNames(richTextContent, className),
    dangerouslySetInnerHTML: {
      __html: sanitizeString(text, sanitizeOptions)
    },
    ref: tooltipRef,
    translate: "no",
    ...containerProps
  }), tooltipNodes.map(container => {
    const id = uniqueId('rich-text-tooltip');
    container.setAttribute('aria-describedby', id);
    return React__default.createElement(Tooltip, {
      key: id,
      container: container,
      id: id
    });
  }));
}

const textReset = {
  margin: 0
};
const useStyles$o = createUseStyles(theme => {
  const {
    fontSize,
    ...questionTitleFormatting
  } = theme.questionTitle;
  const questionTitleFontSize = fontSize !== null && fontSize !== void 0 ? fontSize : {};
  return {
    addonContainer: {
      display: 'inline-block',
      margin: [0, 5],
      verticalAlign: 'text-bottom'
    },
    container: {
      position: 'relative'
    },
    questionNumber: ({
      isRTL
    }) => ({
      margin: isRTL ? '0 0 0 0.25em' : '0 0.25em 0 0'
    }),
    questionTitle: ({
      useDefaultFrontSize
    }) => {
      var _a;

      return { ...textReset,
        ...questionTitleFormatting,
        color: ((_a = theme.questionTitle) === null || _a === void 0 ? void 0 : _a.color) || theme.questionColor,
        fontSize: useDefaultFrontSize && !theme.isAccessible ? theme.fontSize.body : questionTitleFontSize,
        lineHeight: 1.25,
        whiteSpace: 'normal',
        wordWrap: 'break-word'
      };
    },
    requiredAsterisk: ({
      isRTL
    }) => ({
      display: 'inline-block',
      margin: isRTL ? '0 0 0 0.25em' : '0 0.25em 0 0'
    })
  };
});

function RespondentQuestionTitle({
  element: Element = 'div',
  addon,
  heading,
  id,
  number,
  numbered,
  required,
  useDefaultFrontSize = false,
  ...props
}) {
  const {
    isRTL
  } = useContext(L10NContext);
  const {
    addonContainer,
    container,
    questionNumber,
    questionTitle,
    requiredAsterisk
  } = useStyles$o({
    useDefaultFrontSize,
    isRTL
  });
  return React__default.createElement(Element, {
    id: id,
    className: classNames(container, questionTitle),
    translate: "no",
    ...props
  }, required && React__default.createElement("span", {
    "aria-hidden": true,
    className: requiredAsterisk
  }, "*"), numbered && React__default.createElement("span", {
    className: questionNumber
  }, number, "."), React__default.createElement(RichText, {
    element: "span",
    text: heading
  }), addon && React__default.createElement("span", {
    className: addonContainer
  }, addon));
} // default text to be added as a translation to a specific version of Button directly.


const defaultOKConfig = {
  text: 'OK',
  visible: false
};

function QuestionField({
  id,
  title: titleProps,
  okButton: {
    visible: showButton,
    ...okButtonProps
  } = defaultOKConfig,
  error,
  errorId: errorIdProp,
  onLeave,
  padding = {
    top: 0,
    bottom: 0,
    left: 0,
    right: 0
  },
  width,
  children,
  ...props
}) {
  const errorId = errorIdProp !== null && errorIdProp !== void 0 ? errorIdProp : createErrorId(id);
  return React__default.createElement(QuestionFieldLayoutTemplate, {
    footer: showButton && React__default.createElement(Button, {
      type: "button",
      ...okButtonProps
    }),
    hasFooterPadding: true,
    clickShield: false,
    error: error && React__default.createElement(ValidationMessage, {
      id: errorId
    }, error),
    onLeave: onLeave,
    id: id,
    padding: padding,
    width: width,
    title: React__default.createElement(RespondentQuestionTitle, { ...titleProps
    }),
    ...props
  }, children);
}

function SingleTextbox({
  id: questionId,
  required,
  disabled,
  readOnly,
  responses: defaultResponses = [],
  onChange,
  size,
  ...fieldProps
}) {
  const {
    error,
    errorId = createErrorId(questionId),
    title: {
      id: titleId
    }
  } = fieldProps;
  const defaultValue = useMemo(() => {
    var _a, _b;

    return (_b = (_a = defaultResponses.find(r => !!r.value)) === null || _a === void 0 ? void 0 : _a.value) !== null && _b !== void 0 ? _b : '';
  }, // eslint-disable-next-line react-hooks/exhaustive-deps
  []);

  const handleChange = e => {
    const responses = e.target.value ? [{
      id: questionId,
      value: e.target.value
    }] : [];
    onChange === null || onChange === void 0 ? void 0 : onChange(responses);
  };

  return React__default.createElement("div", null, React__default.createElement(QuestionField, {
    id: questionId,
    errorId: errorId,
    "data-testid": "SingleTextboxQuestionType",
    ...fieldProps
  }, React__default.createElement(TextInput$1, {
    name: questionId,
    defaultValue: defaultValue,
    required: required,
    disabled: disabled,
    readOnly: readOnly,
    size: size,
    onChange: handleChange,
    "aria-invalid": !!error,
    "aria-describedby": error && errorId,
    "aria-labelledby": titleId,
    maxLength: TEXT_INPUT_MAX_CHARS
  })));
}

var SingleTextbox$1 = withErrorBoundary(SingleTextbox);

function CommentBox({
  id: questionId,
  required,
  disabled,
  readOnly,
  responses: defaultResponses = [],
  onChange,
  cols = 50,
  rows = 3,
  ...fieldProps
}) {
  const {
    error,
    errorId = createErrorId(questionId),
    title: {
      id: titleId
    }
  } = fieldProps;
  const defaultValue = useMemo(() => {
    var _a, _b;

    return (_b = (_a = defaultResponses.find(r => !!r.value)) === null || _a === void 0 ? void 0 : _a.value) !== null && _b !== void 0 ? _b : '';
  }, // eslint-disable-next-line react-hooks/exhaustive-deps
  []);

  const handleChange = e => {
    const responses = e.target.value ? [{
      id: questionId,
      value: e.target.value
    }] : [];
    onChange === null || onChange === void 0 ? void 0 : onChange(responses);
  };

  return React__default.createElement(QuestionField, {
    id: questionId,
    errorId: errorId,
    "data-testid": "CommentBoxQuestionType",
    ...fieldProps
  }, React__default.createElement(TextArea$1, {
    name: questionId,
    defaultValue: defaultValue,
    disabled: disabled,
    readOnly: readOnly,
    required: required,
    cols: cols,
    rows: rows,
    onChange: handleChange,
    "aria-invalid": !!error,
    "aria-describedby": error && errorId,
    "aria-labelledby": titleId,
    maxLength: TEXT_INPUT_MAX_CHARS
  }));
}

var CommentBox$1 = withErrorBoundary(CommentBox);

function SelectIcon() {
  return React__default.createElement("g", {
    className: "select-icon"
  }, React__default.createElement("polygon", {
    className: "select-arrow select-arrow-top",
    transform: "matrix(1.4619 0 0 1.4619 5.8783 5.4791)",
    points: "1.4514 -3.0633 5.5556 1.0409 -2.6529 1.0409",
    fill: "currentColor",
    stroke: "transparent",
    strokeLinejoin: "round",
    strokeWidth: ".5"
  }), React__default.createElement("polygon", {
    className: "select-arrow select-arrow-bottom",
    transform: "matrix(-1.4619 0 0 -1.4619 10.122 10.522)",
    points: "1.4514 -3.0633 5.5556 1.0409 -2.6529 1.0409",
    fill: "currentColor",
    stroke: "transparent",
    strokeLinejoin: "round",
    strokeWidth: ".5"
  }));
}

var SelectIcon$1 = withSvgIcon(SelectIcon, 'SelectIcon');
const useStyles$n = createUseStyles(theme => {
  const {
    color,
    fontSize,
    fontFamily,
    fontWeight
  } = theme.questionBody;
  return {
    selectContainerStyles: {
      maxWidth: '100%',
      position: 'relative',
      display: 'inline-flex',
      justifyContent: 'flex-start',
      alignItems: 'center',
      overflow: 'hidden',
      transition: 'all 0.1s linear',
      borderRadius: `4px`,
      border: `2px solid transparent`,
      outline: `1px solid transparent`,
      '&:focus-within, &:hover': {
        border: `2px solid ${theme.primaryAccentColor}`,
        outline: `1px solid #fff`
      },
      color: '#000'
    },
    selectStyles: {
      position: 'relative',
      display: 'inline-flex',
      justifyContent: 'flex-start',
      alignItems: 'center',
      overflow: 'hidden',
      fontSize,
      fontFamily,
      fontWeight,
      lineHeight: 'normal',
      outline: 'none',
      backgroundColor: '#fff',
      color: 'inherit',
      maxWidth: '100%',
      padding: '6px 1.75em 6px 6px',
      verticalAlign: 'bottom',
      textSizeAdjust: 'auto',
      // Prevent de fault chevron from showing
      mozAppearance: 'none',
      webkitAppearance: 'none',
      appearance: 'none',
      transition: 'border 0.1s linear',
      border: `1px solid ${color}`,
      '&:focus-within, &:hover': {
        border: `1px solid transparent`
      }
    },
    selectIconStyles: {
      position: 'absolute',
      width: '1em',
      height: '1em',
      display: 'block',
      margin: '0 .5em',
      right: 0,
      zIndex: 2,
      pointerEvents: 'none'
    },
    disabledStyles: {
      opacity: 0.4
    }
  };
});

function NativeSelect({
  required,
  children,
  disabled,
  className,
  icon = React__default.createElement(SelectIcon$1, null),
  ...props
}) {
  const {
    selectStyles,
    selectContainerStyles,
    selectIconStyles,
    disabledStyles
  } = useStyles$n();
  const {
    className: iconClasses,
    ...iconProps
  } = icon.props;
  return React__default.createElement("div", {
    className: classNames(selectContainerStyles, disabled ? disabledStyles : {}, className)
  }, React__default.cloneElement(icon, {
    // this will apply our placement styles, along with any other supplied classes
    className: classNames(selectIconStyles, iconClasses),
    // apply all other props.
    ...iconProps
  }), React__default.createElement("select", {
    className: selectStyles,
    required: required,
    "aria-required": required,
    "data-testid": "select",
    disabled: disabled,
    ...props
  }, children));
}

const useStyles$m = createUseStyles(({
  questionBody: {
    fontFamily,
    fontSize,
    fontWeight
  }
}) => ({
  selectOption: {
    fontFamily,
    fontSize,
    fontWeight
  }
}));

function SelectAnswerOption({
  id,
  label,
  ...props
}) {
  const {
    selectOption
  } = useStyles$m();
  return React__default.createElement("option", {
    id: id,
    "data-testid": "select-answer-option",
    className: selectOption,
    ...props
  }, label);
}
/**
 * Hook to split question-choices by type and filter for visibility
 */


function useQuestionChoices(choices) {
  const options = choices.filter(c => !!c && c.visible);
  return {
    options,

    /** Standard answer options */
    sortableOptions: options.filter(c => !c.type),

    /** None-Of-The-Above (NOTA) option  */
    notaOption: options.find(c => c.type === 'NOTA'),

    /** Other answer option */
    otherOption: options.find(c => c.type === 'ANSWER'),

    /** Other comment option */
    commentOption: options.find(c => c.type === 'COMMENT')
  };
}

const useStyles$l = createUseStyles(theme => {
  var _a;

  const defaultFontSize = theme.fontSize.body;
  const {
    fontFamily = 'inherit',
    fontWeight = 'inherit',
    fontStyle = 'inherit',
    textDecoration = 'inherit',
    highlightColor = 'inherit',
    color = 'inherit'
  } = (_a = theme.questionBody) !== null && _a !== void 0 ? _a : {};
  return {
    otherStyles: {
      marginTop: '5px',
      display: 'block'
    },
    hiddenInput: {
      display: 'none'
    },
    commentChoice: {
      marginTop: 10
    },
    label: {
      display: 'block',
      marginTop: '10px',
      position: 'relative',
      fontFamily,
      fontWeight,
      fontStyle,
      textDecoration,
      highlightColor,
      color,
      fontSize: defaultFontSize
    }
  };
});
/**
 * MultipleChoice Question Type (Tier 2)
 */

function Dropdown({
  id: questionId,
  disabled,
  required: _required,
  readOnly: _readOnly,
  choices = [],
  choiceOther,
  choiceComment,
  responses: defaultResponses = [],
  onChange,
  ...fieldProps
}) {
  const errorId = createErrorId(questionId);
  /** Filter choices that exist and are visible  */

  const {
    options,
    sortableOptions,
    otherOption,
    commentOption
  } = useQuestionChoices([...choices, choiceOther, choiceComment]);
  const [responseValue, setResponseValue] = useState(defaultResponses);
  const {
    otherStyles,
    hiddenInput,
    commentChoice
  } = useStyles$l();
  /**
   * Default value by id
   * used for text-input based choices
   */

  const getDefaultValueById = id => {
    const defaultChoice = defaultResponses.find(c => c.id === id);
    return defaultChoice === null || defaultChoice === void 0 ? void 0 : defaultChoice.value;
  };
  /**
   * Default value for the choices
   */


  const defaultValue = useMemo(() => {
    var _a;

    return (_a = defaultResponses.find(c => c.type !== 'COMMENT')) === null || _a === void 0 ? void 0 : _a.id; // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  /**
   * Other Answer Textfield state
   */

  const [otherText, setOtherText] = useState(otherOption && getDefaultValueById(otherOption.id) || '');

  const handleSelectChange = e => {
    const choiceData = options.find(c => c.id === e.target.value);
    const responses = responseValue.filter(c => c.type === 'COMMENT');

    if (choiceData) {
      const {
        id,
        type
      } = choiceData;

      if (type === 'ANSWER') {
        /** Want to update the value with the value of the TextField texts, when otherAnswer is selected */
        responses.push({
          id,
          type,
          value: otherText
        });
      } else {
        responses.push({
          id,
          type,
          value: e.target.value
        });
      }
    }

    setResponseValue(responses);
    onChange === null || onChange === void 0 ? void 0 : onChange(responses);
  };
  /**
   * Other Answer Textfield Change
   */


  const handleTextChange = e => {
    const choiceData = options.find(c => c.type === 'ANSWER');
    const responses = responseValue.filter(c => c.type === 'COMMENT');

    if (choiceData) {
      const {
        id,
        type
      } = choiceData;
      responses.push({
        id,
        type,
        value: e.target.value
      });
      setOtherText(e.target.value);
    }

    setResponseValue(responses);
    onChange === null || onChange === void 0 ? void 0 : onChange(responses);
  };

  const handleCommentChange = e => {
    const choiceData = options.find(c => c.id === e.target.id);
    const responses = responseValue.filter(c => c.type !== 'COMMENT');

    if (choiceData && !!e.target.value) {
      const {
        id,
        type
      } = choiceData;
      responses.push({
        id,
        type,
        value: e.target.value
      });
    }

    setResponseValue(responses);
    onChange === null || onChange === void 0 ? void 0 : onChange(responses);
  };

  const isOtherAnswerSelected = !!responseValue.find(c => c.type === 'ANSWER');
  const multipleTextLines = otherOption && (otherOption === null || otherOption === void 0 ? void 0 : otherOption.linesCount) > 1;
  return React__default.createElement(QuestionField, {
    id: questionId,
    errorId: errorId,
    "data-testid": "DropdownQuestionType",
    ...fieldProps
  }, React__default.createElement(NativeSelect, {
    defaultValue: defaultValue,
    disabled: disabled,
    onChange: handleSelectChange
  }, React__default.createElement(SelectAnswerOption, {
    value: undefined
  }), " ", sortableOptions === null || sortableOptions === void 0 ? void 0 : sortableOptions.map(choice => {
    return React__default.createElement(SelectAnswerOption, {
      key: choice.id,
      id: choice.id,
      label: choice.label,
      value: choice.id
    });
  }), otherOption && React__default.createElement(SelectAnswerOption, {
    key: otherOption.id,
    id: otherOption.id,
    label: otherOption.label,
    value: otherOption.id
  })), otherOption && (multipleTextLines ? React__default.createElement(TextArea$1, {
    defaultValue: getDefaultValueById(otherOption.id),
    autoFocus: false,
    rows: otherOption.linesCount,
    cols: otherOption.charsCount,
    maxLength: TEXT_INPUT_MAX_CHARS,
    onChange: handleTextChange,
    className: classNames(otherStyles, {
      [hiddenInput]: !isOtherAnswerSelected
    }),
    tabIndex: isOtherAnswerSelected ? 0 : -1
  }) : React__default.createElement(TextInput$1, {
    defaultValue: getDefaultValueById(otherOption.id),
    autoFocus: false,
    autoHeight: true,
    size: otherOption.charsCount,
    maxLength: TEXT_INPUT_MAX_CHARS,
    onChange: handleTextChange,
    className: classNames(otherStyles, {
      [hiddenInput]: !isOtherAnswerSelected
    }),
    tabIndex: isOtherAnswerSelected ? 0 : -1
  })), commentOption && React__default.createElement(CommentChoice, {
    id: commentOption.id,
    label: commentOption.label,
    onChange: handleCommentChange,
    defaultValue: getDefaultValueById(commentOption.id),
    lineCount: commentOption.linesCount,
    charCount: commentOption.charsCount,
    maxLength: TEXT_INPUT_MAX_CHARS,
    "aria-describedby": fieldProps.error && errorId,
    className: commentChoice
  }));
}

var Dropdown$1 = withErrorBoundary(Dropdown);
const useStyles$k = createUseStyles(theme => {
  var _a, _b, _c, _d, _e, _f;

  return {
    textPresentationalStyles: {
      fontFamily: (_a = theme.questionTitle.fontFamily) !== null && _a !== void 0 ? _a : 'inherit',
      fontSize: (_b = theme.questionTitle.fontSize) !== null && _b !== void 0 ? _b : '16px',
      fontWeight: (_c = theme.questionTitle.fontWeight) !== null && _c !== void 0 ? _c : theme.isAccessible ? 500 : 300,
      textDecoration: (_d = theme.questionTitle.textDecoration) !== null && _d !== void 0 ? _d : 'inherit',
      fontStyle: (_e = theme.questionTitle.fontStyle) !== null && _e !== void 0 ? _e : 'inherit',
      color: (_f = theme.questionTitle.color) !== null && _f !== void 0 ? _f : 'inherit'
    },
    buttonStyles: {
      marginTop: '20px'
    }
  };
});

function TextPresentational({
  text,
  padding = {
    top: 0,
    bottom: 0,
    left: 0,
    right: 0
  },
  id,
  okButton = {
    visible: false
  }
}) {
  const richTextId = `text-presentational-${id}`;
  const {
    textPresentationalStyles,
    buttonStyles
  } = useStyles$k();
  const {
    visible: showButton,
    ...buttonProps
  } = okButton;
  return React__default.createElement(QuestionSpacing, {
    padding: padding,
    "data-testid": "TextPresentational"
  }, React__default.createElement(RichText, {
    id: richTextId,
    element: "div",
    text: text,
    className: textPresentationalStyles
  }), showButton && React__default.createElement(Button, { ...buttonProps,
    className: buttonStyles
  }));
}

var TextPresentational$1 = withErrorBoundary(TextPresentational);
const useStyles$j = createUseStyles(() => ({
  imageStyles: {
    maxWidth: '100%',
    display: 'block'
  }
}));

function Image({
  alt = '',
  className,
  ...props
}) {
  const {
    imageStyles
  } = useStyles$j();
  return React__default.createElement("img", {
    className: classNames(imageStyles, className),
    "data-testid": "image",
    alt: alt,
    ...props
  });
}

const useStyles$i = createUseStyles(theme => {
  var _a, _b, _c, _d;

  return {
    presentationalTitleStyles: {
      fontFamily: (_a = theme.questionTitle.fontFamily) !== null && _a !== void 0 ? _a : 'inherit',
      fontSize: (_b = theme.questionTitle.fontSize) !== null && _b !== void 0 ? _b : '16px',
      fontWeight: (_c = theme.questionTitle.fontWeight) !== null && _c !== void 0 ? _c : theme.isAccessible ? 500 : 300,
      marginBottom: '24px',
      textDecoration: (_d = theme.questionTitle.textDecoration) !== null && _d !== void 0 ? _d : 'inherit'
    },
    buttonStyles: {
      marginTop: '20px'
    }
  };
});

function ImagePresentational({
  text,
  image,
  padding = {
    top: 0,
    bottom: 0,
    left: 0,
    right: 0
  },
  id,
  okButton = {
    visible: false
  }
}) {
  const richTextId = `text-presentational-header-${id}`;
  const {
    presentationalTitleStyles,
    buttonStyles
  } = useStyles$i();
  const {
    visible: showButton,
    ...buttonProps
  } = okButton;
  return React__default.createElement(QuestionSpacing, {
    padding: padding,
    "data-testid": "ImagePresentational"
  }, text && React__default.createElement(RichText, {
    id: richTextId,
    element: "div",
    text: text,
    className: presentationalTitleStyles
  }), React__default.createElement(Image, { ...image
  }), showButton && React__default.createElement(Button, { ...buttonProps,
    className: buttonStyles
  }));
}

var ImagePresentational$1 = withErrorBoundary(ImagePresentational);
const radioGroupContext = createContext(undefined);

function useRadioGroup() {
  return useContext(radioGroupContext);
}
/* eslint-disable react-hooks/rules-of-hooks, react-hooks/exhaustive-deps, no-console */


function throwControlChangeError(isControlled, controlled, name, state) {
  const conState = ['controlled', 'uncontrolled'];
  const stateStr = isControlled ? conState : [...conState].reverse();

  if (isControlled !== (controlled !== undefined)) {
    console.error([`@sm/question-ui: A component is changing the ${stateStr[0]} ${state} state of ${name} to be ${stateStr[1]}.`, 'Elements should not switch from uncontrolled to controlled (or vice versa).', `Decide between using a controlled or uncontrolled ${name} element for the lifetime of the component.`, "The nature of the state is determined during the first render. It's considered controlled if the value is not `undefined`.", 'More info: https://fb.me/react-controlled-components'].join('\n'));
  }
}

function throwDefaultStateError(isControlled, defaultValue, defaultProp, name, state) {
  if (!isControlled && defaultValue !== defaultProp) {
    console.error([`@sm/question-ui: A component is changing the default ${state} state of an uncontrolled ${name} after being initialized. `, `To suppress this warning opt to use a controlled ${name}.`].join('\n'));
  }
}

function useControlledState({
  controlled,
  default: defaultProp,
  name,
  state = 'value'
}) {
  // isControlled is ignored in the hook dependency lists as it should never change.
  const {
    current: isControlled
  } = React__default.useRef(controlled !== undefined);
  const [valueState, setValue] = React__default.useState(defaultProp);
  const value = isControlled ? controlled : valueState;

  if (process.env.NODE_ENV !== 'production') {
    const {
      current: defaultValue
    } = React__default.useRef(defaultProp);
    React__default.useEffect(() => {
      throwControlChangeError(isControlled, controlled, name, state);
      throwDefaultStateError(isControlled, defaultValue, defaultProp, name, state);
    }, [state, name, controlled, defaultProp]);
  }

  const setValueIfUncontrolled = React__default.useCallback(newValue => {
    if (!isControlled) {
      setValue(newValue);
    }
  }, [setValue]);
  return [value, setValueIfUncontrolled];
}

function setRef(ref, value) {
  if (typeof ref === 'function') {
    ref(value);
  } else if (ref) {
    // eslint-disable-next-line no-param-reassign
    ref.current = value;
  }
}
/**
 * useForkRef
 * Joins refs together and returns a combination of the two as a new ref
 *
 * @param refA
 * @param refB
 */


function useForkRef(refA, refB) {
  /**
   * This will create a new function if the ref props change and are defined.
   * This means react will call the old forkRef with `null` and the new forkRef
   * with the ref. Cleanup naturally emerges from this behavior
   */
  return useMemo(() => {
    if (refA == null && refB == null) {
      return null;
    }

    return refValue => {
      setRef(refA, refValue);
      setRef(refB, refValue);
    };
  }, [refA, refB]);
}

function useInputStyles({
  onChange,
  ...props
}) {
  var _a;
  /** extract props without affecting passthrough */


  const {
    checked: isChecked,
    defaultChecked: isDefaultChecked,
    disabled: isDisabled,
    'aria-disabled': isAriaDisabled,
    readOnly: isReadOnly,
    'aria-readonly': isAriaReadOnly
  } = props;
  /** configure style-props */

  const disabled = isDisabled || isAriaDisabled === 'true';
  const readOnly = isReadOnly || isAriaReadOnly === 'true';
  const [checked, setIsChecked] = useState((_a = isDefaultChecked !== null && isDefaultChecked !== void 0 ? isDefaultChecked : isChecked) !== null && _a !== void 0 ? _a : false);
  React__default.useEffect(() => {
    setIsChecked(isChecked !== null && isChecked !== void 0 ? isChecked : false);
  }, [isChecked]);
  /** handle change for checked style (controlled/uncontrolled) */

  const handleChange = e => {
    setIsChecked(e.target.checked);
    onChange === null || onChange === void 0 ? void 0 : onChange(e);
  };

  const styleProps = {
    checked,
    disabled,
    readOnly
  };
  const inputProps = { ...props,
    onChange: handleChange
  };
  return {
    styleProps,
    inputProps
  };
}
/**
 * SMQ Tier 3
 */


function RadioGroup({
  children,
  name: nameProp,
  value: valueProp,
  defaultValue,
  onChange,
  disabled = false,
  readOnly = false,
  ...divProps
}) {
  const [value, setValueState] = useControlledState({
    controlled: valueProp,
    default: defaultValue,
    name: 'RadioGroup'
  });

  const handleChange = e => {
    setValueState(e.value);
    onChange === null || onChange === void 0 ? void 0 : onChange(e);
  };

  const name = nameProp;
  return React__default.createElement(radioGroupContext.Provider // eslint-disable-next-line react/jsx-no-constructed-context-values
  , {
    // eslint-disable-next-line react/jsx-no-constructed-context-values
    value: {
      name,
      onChange: handleChange,
      value,
      disabled,
      readOnly
    }
  }, React__default.createElement("div", {
    role: "radiogroup",
    ...divProps
  }, children));
}

const useStyles$h = createUseStyles({
  inputContainer: {
    display: 'inline-flex',
    position: 'relative',
    justifyContent: 'center',
    alignItems: 'center',
    width: '1em',
    minWidth: '1em',
    cursor: 'pointer'
  },
  input: {
    position: 'absolute',
    width: '100%',
    height: '100%',
    opacity: 0.00001,
    zIndex: 1,
    margin: 0,
    cursor: ({
      disabled,
      readOnly
    }) => disabled || readOnly ? 'default' : 'pointer',
    pointerEvents: ({
      disabled,
      readOnly
    }) => disabled || readOnly ? 'none' : undefined
  },
  controlIcon: {
    width: '100%',
    height: '100%',
    opacity: ({
      checked
    }) => checked ? 1 : 0,
    transition: 'opacity .2s linear'
  }
});

function BaseInput({
  element: Element = 'span',
  className,
  icon,
  checked: checkedProp,
  defaultChecked,
  onClick,
  onChange,
  ...inputProps
}, forwardedRef) {
  /** extract props without affecting passthrough */
  const {
    disabled: disabledProp,
    'aria-disabled': ariaDisabledProp,
    readOnly: readOnlyProp,
    'aria-readonly': ariaReadOnlyProp
  } = inputProps;
  const disabled = disabledProp || ariaDisabledProp === 'true';
  const readOnly = readOnlyProp || ariaReadOnlyProp === 'true';
  const [checked, setChecked] = useControlledState({
    controlled: checkedProp,
    default: defaultChecked,
    state: 'checked',
    name: 'BaseInput'
  });

  const handleChange = e => {
    if (e.nativeEvent.defaultPrevented) {
      return;
    }

    if (disabled || readOnly) {
      e.preventDefault();
      return;
    }

    const newChecked = e.target.checked;
    setChecked(newChecked);
    onChange === null || onChange === void 0 ? void 0 : onChange(e);
  };

  const handleClick = e => {
    if (disabled || readOnly) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }

    onClick === null || onClick === void 0 ? void 0 : onClick(e);
  };

  const {
    inputContainer,
    input,
    controlIcon
  } = useStyles$h({
    disabled,
    readOnly,
    checked
  });
  return React__default.createElement(Element, {
    className: classNames(inputContainer, className)
  }, React__default.createElement("input", {
    className: input,
    checked: checkedProp,
    defaultChecked: defaultChecked,
    onChange: handleChange,
    onClick: handleClick,
    ...inputProps,
    ref: forwardedRef
  }), React__default.cloneElement(icon, {
    className: controlIcon
  }));
}

var BaseInput$1 = forwardRef(BaseInput);

function RadioIcon() {
  return React__default.createElement("g", {
    className: "radio-icon"
  }, React__default.createElement("ellipse", {
    fill: "currentColor",
    className: "background",
    transform: "translate(8 8)",
    rx: "8",
    ry: "8"
  }), React__default.createElement("path", {
    fill: "#fff",
    className: "checkmark",
    transform: "matrix(.91924 .91924 -.91924 .91924 -.72152 -9.5796)",
    d: "m14.521 0h1.4788v8h-5.0216l-0.002914-1.4404h3.5456l-1e-6 -6.5596z"
  }));
}

var RadioIcon$1 = withSvgIcon(RadioIcon, 'RadioIcon');
const useStyles$g = createUseStyles(theme => ({
  radioInput: {
    color: theme.questionColor,
    backgroundColor: ({
      checked
    }) => checked ? theme.questionColor : '#fff',
    border: `1px solid ${theme.answerColor}`,
    borderRadius: '100%',
    '& .checkmark': {
      fill: theme.isDark ? '#000' : '#fff'
    },
    '&:focus-within': {
      boxShadow: `0 0 0 1px ${theme.primaryAccentColor}`,
      borderColor: '#fff'
    },
    '& input': {
      cursor: 'pointer'
    }
  }
}));

function RadioInput({
  className,
  ...props
}, forwardedRef) {
  const {
    inputProps,
    styleProps
  } = useInputStyles(props);
  const styles = useStyles$g(styleProps);
  return React__default.createElement(BaseInput$1, {
    type: "radio",
    className: classNames(styles.radioInput, className),
    icon: React__default.createElement(RadioIcon$1, null),
    ref: forwardedRef,
    ...inputProps
  });
}

var RadioInput$1 = forwardRef(RadioInput); // get the margin top by taking the font size and the line height and divide by 2 to get the margin top

const marginTopHelper$1 = (height = 0, lineHeight) => {
  return Math.max(0, (height * lineHeight - height) / 2); // no negative margins
};

const useStyles$f = createUseStyles(theme => {
  var _a;

  return {
    controlLabel: ({
      checked,
      disabled
    }) => {
      const borderStyle = checked ? 'solid' : 'dashed';
      const borderColor = disabled && !checked ? 'transparent' : theme.questionColor;
      return {
        display: 'flex',
        alignItems: 'start',
        cursor: 'pointer',
        padding: [5, 8, 7, 8],
        gap: 8,
        borderRadius: 4,
        border: [1, 'solid', checked ? theme.questionColor : 'transparent'],
        backgroundColor: checked ? theme.input.bgColor : 'inherit',
        ...theme.questionBody,
        lineHeight: 1.5,
        fontWeight: checked ? theme.fontWeight.medium : theme.fontWeight.light,
        '&:hover': {
          border: [1, borderStyle, borderColor]
        },
        '&:focus-within': {
          background: theme.input.bgColor
        }
      };
    },
    icon: {
      flexShrink: 0,
      fontSize: ({
        inputSize
      }) => inputSize !== null && inputSize !== void 0 ? inputSize : '1em',
      marginTop: marginTopHelper$1((_a = theme.questionBody) === null || _a === void 0 ? void 0 : _a.fontSize, 1.5),
      // The following selector singles out Safari and Chrome on iOS14 to
      // implement a workaround for the lack of flex gap.
      '@supports (-webkit-touch-callout: none) and (not (translate: none))': {
        marginRight: 8
      }
    }
  };
});

function Radio({
  children: label = '',
  className,
  onChange,
  checked: checkedProp,
  name: nameProp,
  disabled: disabledProp,
  readOnly: readOnlyProp,
  inputSize,
  ...props
}, forwardedRef) {
  var _a, _b;

  const radioGroup = useRadioGroup();
  let checked = checkedProp;
  let name = nameProp;

  if (radioGroup) {
    if (typeof checked === 'undefined') {
      checked = radioGroup.value === props.value;
    }

    if (typeof name === 'undefined') {
      name = radioGroup.name;
    }
  }

  const disabled = (_a = radioGroup === null || radioGroup === void 0 ? void 0 : radioGroup.disabled) !== null && _a !== void 0 ? _a : disabledProp;
  const readOnly = (_b = radioGroup === null || radioGroup === void 0 ? void 0 : radioGroup.readOnly) !== null && _b !== void 0 ? _b : readOnlyProp;

  const handleChange = e => {
    const {
      id,
      checked: isChecked,
      value
    } = e.target;
    radioGroup === null || radioGroup === void 0 ? void 0 : radioGroup.onChange({
      id,
      checked: isChecked,
      value
    });
    onChange === null || onChange === void 0 ? void 0 : onChange({
      id,
      checked: isChecked,
      value
    });
  };

  const {
    inputProps,
    styleProps
  } = useInputStyles({ ...props,
    name,
    checked,
    disabled,
    readOnly,
    onChange: handleChange
  });
  const {
    controlLabel,
    icon
  } = useStyles$f({ ...styleProps,
    inputSize
  });
  return React__default.createElement("label", {
    tabIndex: -1,
    htmlFor: props.id,
    className: classNames(controlLabel, className)
  }, React__default.createElement(RadioInput$1, { ...inputProps,
    ref: forwardedRef,
    className: icon
  }), React__default.createElement(RichText, {
    element: "span",
    text: label
  }));
}

var Radio$1 = forwardRef(Radio);
const useStyles$e = createUseStyles(theme => ({
  radioInput: {
    '& label': {
      alignItems: 'baseline'
    }
  },
  labelContainer: {
    display: 'flex',
    flexDirection: 'column'
  },
  textInput: {
    opacity: ({
      checked
    }) => checked ? 1 : 0.5,
    cursor: ({
      checked
    }) => checked ? 'inherit' : 'pointer',
    marginTop: 5,
    marginLeft: 32,
    maxWidth: 'calc(100% - 32px)',
    [`@media (max-width: ${theme.breakpoints.xxs.max})`]: {
      marginLeft: 0,
      maxWidth: '100%'
    },
    // overwrite TextInput styles
    fontSize: theme.questionBody.fontSize,
    lineHeight: 'normal'
  },
  checkHover: {
    '&:hover': {
      outline: 'none'
    }
  }
}));

function RadioTextfield({
  refs,
  ...props
}) {
  const {
    id,
    children: label,
    checked: checkedProp = false,
    value: valueProp,
    defaultValue,
    lineCount = 1,
    charCount = 50,
    maxLength,
    onClick,
    onChange,
    ...radioProps
  } = props;
  const radioGroup = useRadioGroup();
  const [checked, setChecked] = useState(checkedProp);
  const [value, setValue] = useState(defaultValue || valueProp);
  const radioInputRef = useRef(null);
  const radioRef = useForkRef(radioInputRef, refs === null || refs === void 0 ? void 0 : refs.radio);
  const textInputRef = useRef(null);
  const inputRef = useForkRef(textInputRef, refs === null || refs === void 0 ? void 0 : refs.text);
  const multipleTextLines = lineCount > 1;

  const handleValueChange = e => {
    radioGroup === null || radioGroup === void 0 ? void 0 : radioGroup.onChange(e);
    setValue(e.value);
    onChange === null || onChange === void 0 ? void 0 : onChange(e);
  };

  const handleInputChange = e => {
    handleValueChange({
      id,
      value: e.target.value,
      checked
    });
  };

  const handleFocusChange = e => {
    handleValueChange({
      id,
      value: e.target.value,
      checked
    });
  };

  const handleClick = e => {
    var _a;
    /**
     * Selection via keyboard calls onClick event - check for
     * mouse-position to ensure we were called by a pointing device.
     */


    if (!checked && e.clientX !== 0 && e.clientY !== 0) {
      (_a = textInputRef === null || textInputRef === void 0 ? void 0 : textInputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
    }

    setChecked(true);
    onClick === null || onClick === void 0 ? void 0 : onClick(e);
  };

  useEffect(() => {
    setChecked(checkedProp);
  }, [checkedProp]);
  useEffect(() => {
    if ((radioGroup === null || radioGroup === void 0 ? void 0 : radioGroup.value) !== value) {
      setChecked(false);
    }
  }, [radioGroup, value]);
  const {
    radioInput,
    textInput,
    checkHover
  } = useStyles$e({
    checked
  });
  return React__default.createElement(React__default.Fragment, null, React__default.createElement(Radio$1, {
    id: `radio-input-${id}`,
    checked: checked,
    value: value,
    onClick: handleClick,
    onChange: handleValueChange,
    className: radioInput,
    ref: radioRef,
    ...radioProps
  }, label), multipleTextLines ? React__default.createElement(TextArea$1, {
    id: `radio-field-${id}`,
    defaultValue: defaultValue,
    autoFocus: false,
    rows: lineCount,
    cols: charCount,
    maxLength: maxLength,
    onClick: handleClick,
    onChange: handleInputChange,
    onBlur: handleFocusChange,
    onFocus: handleFocusChange,
    className: classNames(textInput, {
      [checkHover]: !checked
    }),
    ref: inputRef,
    tabIndex: checked ? 0 : -1
  }) : React__default.createElement(TextInput$1, {
    id: `radio-field-${id}`,
    defaultValue: defaultValue,
    autoFocus: false,
    autoHeight: true,
    size: charCount,
    maxLength: maxLength,
    onClick: handleClick,
    onChange: handleInputChange,
    onBlur: handleFocusChange,
    onFocus: handleFocusChange,
    className: classNames(textInput, {
      [checkHover]: !checked
    }),
    ref: inputRef,
    tabIndex: checked ? 0 : -1
  }));
}

const useStyles$d = createUseStyles({
  commentChoiceContainer: {
    marginTop: '10px'
  }
});
const INPUT_SIZE$2 = 20;
/**
 * MultipleChoice Question Type (Tier 2)
 */

function MultipleChoice({
  id: questionId,
  required,
  disabled,
  readOnly,
  columnLayout = 1,
  choices = [],
  choiceNoneOfTheAbove,
  choiceOther,
  choiceComment,
  responses: defaultResponses = [],
  onChange,
  ...fieldProps
}) {
  const groupId = createFieldId(questionId);
  const errorId = createErrorId(questionId);
  /** Filter choices that exist and are visible  */

  const {
    options,
    sortableOptions,
    notaOption,
    otherOption,
    commentOption
  } = useQuestionChoices([...choices, choiceNoneOfTheAbove, choiceOther, choiceComment]);
  const [responseValue, setResponseValue] = useState(defaultResponses);
  /**
   * Default value by id
   * used for text-input based choices
   */

  const getDefaultValueById = id => {
    const defaultChoice = defaultResponses.find(c => c.id === id);
    return defaultChoice === null || defaultChoice === void 0 ? void 0 : defaultChoice.value;
  };
  /**
   * Default value for the group
   */


  const defaultValue = useMemo(() => {
    var _a;

    return (_a = defaultResponses.find(c => c.type !== 'COMMENT')) === null || _a === void 0 ? void 0 : _a.value;
  }, // defaultValues should not be changed post render (breaks rules of controlled components)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  []);
  /**
   * Determine if choice is selected
   * @return TRUE if the choice.id exists in responses
   */

  const isChecked = id => {
    return !!responseValue.find(c => c.id === id);
  };

  const handleRadioChange = e => {
    const choiceData = options.find(c => e.id.match(c.id));
    const responses = responseValue.filter(c => c.type === 'COMMENT');

    if (choiceData) {
      const {
        id,
        type
      } = choiceData;
      responses.push({
        id,
        type,
        value: e.value
      });
    }

    setResponseValue(responses);
    onChange === null || onChange === void 0 ? void 0 : onChange(responses);
  };

  const handleCommentChange = e => {
    const choiceData = options.find(c => c.id === e.target.id);
    const responses = responseValue.filter(c => c.type !== 'COMMENT');

    if (choiceData && !!e.target.value) {
      const {
        id,
        type
      } = choiceData;
      responses.push({
        id,
        type,
        value: e.target.value
      });
    }

    setResponseValue(responses);
    onChange === null || onChange === void 0 ? void 0 : onChange(responses);
  };
  /**
   * deselect choice if the question is not required
   */


  const unCheckOption = () => {
    if (required) {
      return;
    }

    const responses = responseValue.filter(c => c.type === 'COMMENT');
    setResponseValue(responses);
    onChange === null || onChange === void 0 ? void 0 : onChange(responses);
  };

  const handleClick = e => {
    // match is used since RadioTextfield applies a prefix
    // to maintain unique ID's'
    const input = e.target;
    const isSelected = responseValue.find(c => input.id.match(c.id));

    if (isSelected && input.checked) {
      e.preventDefault();
      unCheckOption();
    }
  };

  const handleKeyboard = e => {
    const isSelected = responseValue.find(c => e.target.id.match(c.id));

    if (isSelected && e.code === 'Space') {
      e.preventDefault();
      unCheckOption();
    }
  };

  const radioEvents = {
    onClick: handleClick,
    onKeyDown: handleKeyboard
  };
  const radioGroupProps = {
    disabled,
    readOnly,
    name: groupId,
    defaultValue,
    onChange: handleRadioChange
  };
  const {
    commentChoiceContainer
  } = useStyles$d();
  return React__default.createElement(QuestionField, {
    id: questionId,
    errorId: errorId,
    "data-testid": "MultipleChoiceQuestionType",
    ...fieldProps
  }, React__default.createElement(RadioGroup, { ...radioGroupProps
  }, React__default.createElement(QuestionAnswerLayoutTemplate, {
    gridCellMargin: [0, 2, 5, 2],
    otherCellMargin: [0, 2, 5, 2],
    columns: columnLayout,
    other: otherOption && React__default.createElement(RadioTextfield, {
      id: otherOption.id,
      checked: isChecked(otherOption.id),
      defaultValue: getDefaultValueById(otherOption.id),
      lineCount: otherOption.linesCount,
      charCount: otherOption.charsCount,
      maxLength: TEXT_INPUT_MAX_CHARS,
      "aria-describedby": fieldProps.error && errorId,
      inputSize: INPUT_SIZE$2,
      ...radioEvents
    }, otherOption.label),
    noneOfTheAbove: notaOption && React__default.createElement(Radio$1, {
      id: notaOption.id,
      value: notaOption.id,
      checked: isChecked(notaOption.id),
      "aria-describedby": fieldProps.error && errorId,
      inputSize: INPUT_SIZE$2,
      ...radioEvents
    }, notaOption.label)
  }, sortableOptions.map(choice => React__default.createElement(Radio$1, {
    id: choice.id,
    key: choice.id,
    value: choice.id,
    checked: isChecked(choice.id),
    "aria-describedby": fieldProps.error && errorId,
    inputSize: INPUT_SIZE$2,
    ...radioEvents
  }, choice.label))), commentOption && React__default.createElement(CommentChoice, {
    id: commentOption.id,
    label: commentOption.label,
    onChange: handleCommentChange,
    defaultValue: getDefaultValueById(commentOption.id),
    lineCount: commentOption.linesCount,
    charCount: commentOption.charsCount,
    maxLength: TEXT_INPUT_MAX_CHARS,
    "aria-describedby": fieldProps.error && errorId,
    className: commentChoiceContainer
  })));
}

function CheckboxIcon() {
  return React__default.createElement("g", {
    className: "checkbox-icon"
  }, React__default.createElement("rect", {
    fill: "currentColor",
    className: "background",
    width: "16",
    height: "16",
    rx: "2",
    ry: "2"
  }), React__default.createElement("path", {
    fill: "#fff",
    className: "checkmark",
    transform: "matrix(.91924 .91924 -.91924 .91924 -.72152 -9.5796)",
    d: "m14.521 0h1.4788v8h-5.0216l-0.002914-1.4404h3.5456l-1e-6 -6.5596z"
  }));
}

var CheckboxIcon$1 = withSvgIcon(CheckboxIcon, 'CheckboxIcon');

function CaretDown() {
  return React__default.createElement("g", {
    className: "caret-down-icon"
  }, React__default.createElement("path", {
    d: "M13.254 4c.19.001.38.088.526.26.267.316.29.81.073 1.158l-.073.1L8.53 11.74l-.084.086a.66.66 0 0 1-.901-.007l-.076-.08L2.22 5.523l-.073-.1a1.024 1.024 0 0 1 0-1.058l.073-.1.084-.085a.674.674 0 0 1 .446-.175V4h10.504z",
    fill: "currentColor"
  }));
}

var CaretDownIcon = withSvgIcon(CaretDown, 'CaretDownIcon');

function CaretDownOutline() {
  return React__default.createElement("g", {
    className: "caret-down-icon"
  }, React__default.createElement("path", {
    d: "m922.74 642.16l-415.95-416.06c-18.533-18.538-48.507-18.538-67.04 0l-415.85 416.06c-18.533 18.538-18.533 48.519 0 67.057l33.473 33.481c18.533 18.538 48.507 18.538 67.04 0l348.91-349 348.91 349c18.533 18.538 48.507 18.538 67.04 0l33.473-33.481c18.533-18.538 18.533-48.519 0-67.057z",
    transform: "matrix(-.016568 0 0 -.016568 15.842 16.026)",
    fill: "currentcolor"
  }));
}

var CaretDownOutlineIcon = withSvgIcon(CaretDownOutline, 'CaretDownOutlineIcon');

function CaretLeftOutline() {
  return React__default.createElement("g", {
    className: "caret-left-icon"
  }, React__default.createElement("path", {
    transform: "matrix(0 -.016568 .016568 0 -.025552 15.842)",
    d: "m922.74 642.16-415.95-416.06c-18.533-18.538-48.507-18.538-67.04 0l-415.85 416.06c-18.533 18.538-18.533 48.519 0 67.057l33.473 33.481c18.533 18.538 48.507 18.538 67.04 0l348.91-349 348.91 349c18.533 18.538 48.507 18.538 67.04 0l33.473-33.481c18.533-18.538 18.533-48.519 0-67.057z",
    fill: "currentcolor"
  }));
}

withSvgIcon(CaretLeftOutline, 'CaretLeftOutlineIcon');

function CaretRightOutline() {
  return React__default.createElement("g", {
    className: "caret-right-icon"
  }, React__default.createElement("path", {
    transform: "matrix(0 .016568 -.016568 0 16.026 .15803)",
    d: "m922.74 642.16-415.95-416.06c-18.533-18.538-48.507-18.538-67.04 0l-415.85 416.06c-18.533 18.538-18.533 48.519 0 67.057l33.473 33.481c18.533 18.538 48.507 18.538 67.04 0l348.91-349 348.91 349c18.533 18.538 48.507 18.538 67.04 0l33.473-33.481c18.533-18.538 18.533-48.519 0-67.057z",
    fill: "currentcolor"
  }));
}

withSvgIcon(CaretRightOutline, 'CaretRightOutlineIcon');
const useStyles$c = createUseStyles(theme => ({
  checkboxInput: ({
    checked,
    disabled,
    readOnly
  }) => {
    const checkBgColor = checked ? theme.questionColor : '#fff';
    const backgroundColor = disabled || readOnly ? '#aaa' : checkBgColor;
    return {
      color: disabled || readOnly ? '#aaa' : theme.questionColor,
      borderRadius: 2,
      backgroundColor,
      border: `1px solid ${disabled || readOnly ? '#aaa' : theme.questionColor}`,
      '& .checkmark': {
        fill: theme.isDark ? '#000' : '#fff'
      },
      '&:focus-within': {
        boxShadow: `0 0 0 1px ${theme.primaryAccentColor}`,
        borderColor: '#fff'
      },
      '& input': {
        cursor: 'pointer'
      }
    };
  }
}));

function CheckboxInput({
  className,
  ...props
}, ref) {
  const {
    inputProps,
    styleProps
  } = useInputStyles(props);
  const {
    checkboxInput
  } = useStyles$c(styleProps);
  return React__default.createElement(BaseInput$1, {
    ref: ref,
    type: "checkbox",
    className: classNames(checkboxInput, className),
    icon: React__default.createElement(CheckboxIcon$1, null),
    ...inputProps
  });
}

var CheckboxInput$1 = React__default.forwardRef(CheckboxInput); // get the margin top by taking the font size and the line height and divide by 2 to get the margin top

const marginTopHelper = (height = 0, lineHeight) => {
  return Math.max(0, (height * lineHeight - height) / 2); // no negative margins
};

const useStyles$b = createUseStyles(theme => {
  var _a;

  return {
    checkboxContainer: ({
      checked,
      disabled
    }) => {
      const borderStyle = checked ? 'solid' : 'dashed';
      const borderColor = disabled && !checked ? 'transparent' : theme.questionColor;
      return {
        display: 'flex',
        alignItems: 'start',
        cursor: 'pointer',
        padding: [5, 8, 7, 8],
        gap: 8,
        borderRadius: 4,
        border: [1, 'solid', checked ? theme.questionColor : 'transparent'],
        backgroundColor: checked ? theme.input.bgColor : 'inherit',
        ...theme.questionBody,
        lineHeight: 1.5,
        fontWeight: checked ? theme.fontWeight.medium : theme.fontWeight.light,
        '&:hover': {
          border: [1, borderStyle, borderColor]
        },
        '&:focus-within': {
          background: theme.input.bgColor
        }
      };
    },
    icon: {
      flexShrink: 0,
      fontSize: ({
        inputSize
      }) => inputSize !== null && inputSize !== void 0 ? inputSize : '1em',
      marginTop: marginTopHelper((_a = theme.questionBody) === null || _a === void 0 ? void 0 : _a.fontSize, 1.5),
      // The following selector singles out Safari and Chrome on iOS14 to
      // implement a workaround for the lack of flex gap.
      '@supports (-webkit-touch-callout: none) and (not (translate: none))': {
        marginRight: 8
      }
    }
  };
});

function Checkbox({
  children: label = '',
  inputSize,
  onChange,
  ...props
}, ref) {
  const handleChange = e => {
    onChange === null || onChange === void 0 ? void 0 : onChange({
      id: e.target.id,
      checked: e.target.checked,
      value: e.target.value
    });
  };

  const {
    inputProps,
    styleProps
  } = useInputStyles({ ...props,
    onChange: handleChange
  });
  const {
    checkboxContainer,
    icon
  } = useStyles$b({ ...styleProps,
    inputSize
  });
  return React__default.createElement("label", {
    htmlFor: inputProps.id,
    className: checkboxContainer,
    tabIndex: -1
  }, React__default.createElement(CheckboxInput$1, {
    ref: ref,
    ...inputProps,
    className: icon
  }), React__default.createElement(RichText, {
    element: "span",
    text: label
  }));
}

var Checkbox$1 = forwardRef(Checkbox);
const useStyles$a = createUseStyles(theme => ({
  checkbox: {
    '& label': {
      alignItems: 'baseline'
    }
  },
  labelContainer: {
    display: 'flex',
    flexDirection: 'column'
  },
  textInput: {
    opacity: ({
      isChecked
    }) => isChecked ? 1 : 0.5,
    marginTop: 5,
    marginLeft: 32,
    maxWidth: 'calc(100% - 32px)',
    [`@media (max-width: ${theme.breakpoints.xxs.max})`]: {
      marginLeft: 0,
      maxWidth: '100%'
    },
    // overwrite TextInput styles
    fontSize: theme.questionBody.fontSize,
    lineHeight: 'normal'
  },
  checkHover: {
    '&:hover': {
      outline: 'none',
      cursor: 'pointer'
    }
  }
}));

function CheckboxTextfield(props) {
  const {
    id,
    children: label,
    value: inputValue = '',
    defaultValue,
    maxLength,
    onClick,
    onKeyDown,
    onChange,
    checked: checkedProp = false,
    ...checkboxProps
  } = props;
  const [isChecked, setIsChecked] = useState(checkedProp);
  const [value, setValue] = React__default.useState(defaultValue || inputValue);
  const {
    checkbox,
    textInput,
    checkHover
  } = useStyles$a({
    isChecked
  });

  const handleValueChange = e => {
    setValue(e.value);
    onChange === null || onChange === void 0 ? void 0 : onChange({
      id,
      value: e.value,
      checked: e.checked
    });
  };

  const handleInputChange = e => {
    if (!isChecked) {
      setIsChecked(true);
    }

    handleValueChange({
      id,
      value: e.target.value,
      checked: true
    });
  };

  const inputRef = React__default.useRef(null);

  const handleCheckboxClick = e => {
    var _a;

    if (!isChecked && e.clientX !== 0 && e.clientY !== 0) {
      (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
    }

    setIsChecked(!isChecked);
    onClick === null || onClick === void 0 ? void 0 : onClick(e);
  };

  const handleTextboxClick = () => {
    if (!isChecked) {
      setIsChecked(true);
    }

    handleValueChange({
      id,
      value,
      checked: true
    });
  };

  const handleKeyDown = e => {
    if (e.code === 'Space') {
      e.preventDefault();
      setIsChecked(!isChecked);
      handleValueChange({
        id,
        value,
        checked: !isChecked
      });
      return;
    }

    onKeyDown === null || onKeyDown === void 0 ? void 0 : onKeyDown(e);
  };

  React__default.useEffect(() => {
    setIsChecked(checkedProp);
  }, [checkedProp]);
  return React__default.createElement(React__default.Fragment, null, React__default.createElement(Checkbox$1, {
    id: `checkbox-input-${id}`,
    checked: isChecked,
    onKeyDown: handleKeyDown,
    onClick: handleCheckboxClick,
    onChange: handleValueChange,
    className: checkbox,
    value: value,
    maxLength: maxLength,
    ...checkboxProps
  }, label), React__default.createElement(TextInput$1, {
    id: `checkbox-field-${id}`,
    onChange: handleInputChange,
    onClick: handleTextboxClick,
    className: classNames(textInput, {
      [checkHover]: !isChecked
    }),
    defaultValue: defaultValue,
    autoHeight: true,
    maxLength: maxLength,
    tabIndex: isChecked ? 0 : -1,
    ref: inputRef
  }));
}

const useStyles$9 = createUseStyles({
  commentChoiceContainer: {
    marginTop: '10px'
  }
});
const INPUT_SIZE$1 = 20;

function CheckboxQuestion({
  id: questionId,
  required: _req,
  readOnly: _ro,
  disabled,
  columnLayout,
  choices = [],
  responses = [],
  choiceNoneOfTheAbove,
  choiceOther,
  choiceComment,
  onChange,
  ...questionFieldProps
}) {
  const [selectedChoices, setSelectedChoices] = useState(responses);
  const errorId = createErrorId(questionId);
  /**
   * Default value by id
   * used for text-input based choices
   */

  const getDefaultValueById = id => {
    var _a;

    return (_a = responses.find(c => c.id === id)) === null || _a === void 0 ? void 0 : _a.value;
  };

  const handleDefault = id => {
    var _a;

    return (_a = responses.find(c => c.id === id)) === null || _a === void 0 ? void 0 : _a.value;
  };

  const isChecked = id => !!selectedChoices.find(item => item.id === id);

  const handleChange = e => {
    // 1. remove NOTA from array
    const newState = selectedChoices.filter(c => c.id !== e.id && c.type !== 'NOTA'); // 2. if e.target.checked remove item from array

    if (e.checked) {
      newState.push({
        id: e.id,
        value: e.value
      });
    }

    setSelectedChoices(newState);
    onChange === null || onChange === void 0 ? void 0 : onChange(newState);
  };

  const handleNAChange = e => {
    const newState = selectedChoices.filter(c => c.type === 'COMMENT'); // 2. if e.target.checked remove item from array

    if (e.checked) {
      newState.push({
        id: e.id,
        value: e.value,
        type: 'NOTA'
      });
    }

    setSelectedChoices(newState);
    onChange === null || onChange === void 0 ? void 0 : onChange(newState);
  };

  const handleOtherChange = e => {
    const newState = selectedChoices.filter(c => c.id !== e.id && c.type !== 'NOTA'); // 2. if e.target.checked remove item from array

    if (e.checked) {
      newState.push({
        id: e.id,
        value: e.value,
        type: 'ANSWER'
      });
    }

    setSelectedChoices(newState);
    onChange === null || onChange === void 0 ? void 0 : onChange(newState);
  };

  const handleCommentChange = e => {
    const newState = selectedChoices.filter(c => c.type !== 'COMMENT');

    if (newState && !!e.target.value) {
      newState.push({
        id: e.target.id,
        value: e.target.value,
        type: 'COMMENT'
      });
    }

    setSelectedChoices(newState);
    onChange === null || onChange === void 0 ? void 0 : onChange(newState);
  };

  const {
    commentChoiceContainer
  } = useStyles$9();
  return React__default.createElement(QuestionField, {
    id: questionId,
    "data-testid": "CheckboxQuestionType",
    ...questionFieldProps
  }, React__default.createElement(QuestionAnswerLayoutTemplate, {
    gridCellMargin: [0, 2, 5, 2],
    otherCellMargin: [0, 2, 5, 2],
    noneOfTheAbove: choiceNoneOfTheAbove && (choiceNoneOfTheAbove === null || choiceNoneOfTheAbove === void 0 ? void 0 : choiceNoneOfTheAbove.visible) && React__default.createElement(Checkbox$1, {
      id: choiceNoneOfTheAbove.id,
      value: choiceNoneOfTheAbove.id,
      onChange: handleNAChange,
      checked: isChecked(choiceNoneOfTheAbove.id),
      disabled: disabled,
      readOnly: _ro,
      "aria-describedby": questionFieldProps.error && errorId || undefined,
      inputSize: INPUT_SIZE$1
    }, choiceNoneOfTheAbove.label),
    other: (choiceOther === null || choiceOther === void 0 ? void 0 : choiceOther.type) === 'ANSWER' && (choiceOther === null || choiceOther === void 0 ? void 0 : choiceOther.visible) && React__default.createElement(CheckboxTextfield, {
      id: choiceOther.id,
      defaultValue: handleDefault(choiceOther.id),
      onChange: handleOtherChange,
      checked: isChecked(choiceOther.id),
      maxLength: TEXT_INPUT_MAX_CHARS,
      disabled: disabled,
      readOnly: _ro,
      "aria-describedby": questionFieldProps.error && errorId || undefined,
      inputSize: INPUT_SIZE$1
    }, choiceOther.label),
    columns: columnLayout
  }, choices === null || choices === void 0 ? void 0 : choices.map(choice => {
    return React__default.createElement(Checkbox$1, {
      key: choice.id,
      id: choice.id,
      onChange: handleChange,
      checked: isChecked(choice.id),
      value: choice.id,
      disabled: disabled,
      readOnly: _ro,
      "aria-describedby": questionFieldProps.error && errorId || undefined,
      inputSize: INPUT_SIZE$1
    }, choice.label);
  })), choiceComment && React__default.createElement(CommentChoice, {
    id: choiceComment.id,
    label: choiceComment.label,
    onChange: handleCommentChange,
    defaultValue: getDefaultValueById(choiceComment.id),
    lineCount: choiceComment.linesCount,
    charCount: choiceComment.charsCount,
    maxLength: TEXT_INPUT_MAX_CHARS,
    "aria-describedby": questionFieldProps.error && errorId || undefined,
    className: commentChoiceContainer
  }));
}

var CheckboxQuestion$1 = withErrorBoundary(CheckboxQuestion);
const useStyles$8 = createUseStyles(theme => ({
  inputContainer: {
    display: 'flex',
    position: 'relative'
  },
  input: {
    position: 'absolute',
    cursor: ({
      disabled,
      readOnly
    }) => disabled || readOnly ? 'default' : 'pointer',
    pointerEvents: ({
      disabled,
      readOnly
    }) => disabled || readOnly ? 'none' : undefined
  },
  labelContainer: ({
    checked,
    disabled
  }) => {
    var _a;

    const borderColor = disabled && !checked ? 'transparent' : theme.primaryAccentColor;
    return {
      display: 'flex',
      position: 'relative',
      flexDirection: 'column',
      cursor: 'pointer',
      border: [1, 'solid', checked ? theme.questionColor : (_a = theme.questionBody.highlightColor) !== null && _a !== void 0 ? _a : 'rgb(208, 210, 211)'],
      borderRadius: 2,
      backgroundColor: checked ? theme.input.bgColor : 'inherit',
      ...theme.questionBody,
      '&:hover': {
        border: [1, 'solid', borderColor]
      },
      '&:focus-within': {
        border: [1, 'solid', borderColor]
      }
    };
  },
  controlImage: {
    position: 'absolute',
    right: '-1',
    top: '-1',
    width: '26px',
    height: '26px',
    transitionDuration: '0.3s',
    border: 'none',
    borderRadius: 2,
    zIndex: 10,
    transform: ({
      checked
    }) => checked ? 'scale(1)' : 'scale(0)'
  },
  questionStyle: ({
    checked
  }) => {
    var _a;

    return {
      padding: '9px 7px',
      width: '100%',
      wordWrap: 'break-word',
      fontWeight: checked ? 'bold' : 'normal',
      '&:focus-within': {
        background: (_a = theme.questionBody.highlightColor) !== null && _a !== void 0 ? _a : 'rgb(208, 210, 211)'
      }
    };
  },
  imageContainer: {
    height: '168px',
    width: '100%',
    display: 'flex',
    justifyContent: 'flex-end',
    backgroundColor: '#EFEFEF',
    '& img': {
      objectFit: 'contain',
      width: '100%'
    }
  }
}));
const useStyles$7 = createUseStyles(theme => ({
  imageInput: ({
    checked,
    disabled,
    readOnly
  }) => {
    const checkBgColor = checked ? theme.questionColor : 'transparent';
    const backgroundColor = disabled || readOnly ? '#aaa' : checkBgColor;
    return {
      color: disabled || readOnly ? '#aaa' : theme.input.activeColor,
      border: 'none',
      borderRadius: 4,
      backgroundColor,
      '& .checkmark': {
        fill: contrastColor(theme.input.activeColor)
      },
      '& input': {
        cursor: 'pointer'
      }
    };
  }
}));

function ImageInput({
  className,
  type,
  ...props
}, ref) {
  const {
    inputProps,
    styleProps
  } = useInputStyles(props);
  const {
    imageInput
  } = useStyles$7(styleProps);
  return React__default.createElement(BaseInput$1, {
    ref: ref,
    type: type,
    className: classNames(imageInput, className),
    icon: React__default.createElement(CheckboxIcon$1, null),
    ...inputProps
  });
}

var ImageInput$1 = React__default.forwardRef(ImageInput);

function ImageChoice({
  id,
  type,
  alt = '',
  label,
  src: imageSrc,
  className,
  checked,
  name,
  onChange,
  disabled: disabledProp,
  'aria-disabled': ariaDisabledProp,
  readOnly: readOnlyProp,
  'aria-readonly': ariaReadOnlyProp,
  ...inputProps
}, ref) {
  const handleChange = e => {
    onChange === null || onChange === void 0 ? void 0 : onChange({
      id: e.target.id,
      checked: e.target.checked,
      value: e.target.value
    });
  };

  const disabled = disabledProp || ariaDisabledProp === 'true';
  const readOnly = readOnlyProp || ariaReadOnlyProp === 'true';
  const {
    inputContainer,
    controlImage,
    imageContainer,
    labelContainer,
    questionStyle
  } = useStyles$8({
    disabled,
    readOnly,
    checked
  });
  const imageId = `${id}-image`;
  const labelId = `${id}-label`;
  const ariaDescribeId = alt ? imageId : undefined;
  return React__default.createElement("div", {
    className: inputContainer
  }, React__default.createElement("label", {
    htmlFor: id,
    className: classNames(labelContainer, className),
    tabIndex: -1
  }, React__default.createElement("div", {
    className: imageContainer
  }, React__default.createElement(ImageInput$1, {
    "aria-labelledby": labelId,
    "aria-describedby": ariaDescribeId,
    ref: ref,
    checked: checked,
    onChange: handleChange,
    className: controlImage,
    id: id,
    type: type,
    value: id,
    name: name,
    ...inputProps
  }), React__default.createElement("img", {
    id: imageId,
    src: imageSrc,
    alt: alt
  })), label && React__default.createElement("span", {
    id: labelId,
    className: questionStyle
  }, label)));
}

var ImageChoice$1 = forwardRef(ImageChoice);
const useStyles$6 = createUseStyles(theme => {
  var _a, _b;

  return {
    imageChoiceContainer: {
      display: 'grid',
      flexWrap: 'wrap',
      gap: '25px',
      width: '100%',
      gridTemplateColumns: 'minmax(0, 1fr)',
      [`@media (min-width: ${(_a = theme.breakpoints) === null || _a === void 0 ? void 0 : _a.sm.min})`]: {
        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))'
      },
      [`@media (min-width: ${(_b = theme.breakpoints) === null || _b === void 0 ? void 0 : _b.lg.min})`]: {
        gridTemplateColumns: 'repeat(3, minmax(0, 1fr))'
      }
    },
    imageChoice: {
      width: '100%'
    },
    noneOfTheAboveContainer: {
      marginTop: '15px'
    }
  };
});
const INPUT_SIZE = 20;

function ImageChoiceQuestion({
  id: questionId,
  readOnly,
  disabled,
  multiple = false,
  choices = [],
  choiceNoneOfTheAbove,
  onChange,
  responses = [],
  ...questionFieldProps
}) {
  const imageChoiceType = multiple ? 'checkbox' : 'radio';
  const groupName = !multiple ? `${questionId}-imageChoice` : undefined;
  const errorId = createErrorId(questionId);
  const [selectedChoices, setSelectedChoices] = useState(responses);

  const handleChange = e => {
    const newState = !multiple ? [] : selectedChoices.filter(c => c.id !== e.id && c.type !== 'NOTA');

    if (e.checked) {
      newState.push({
        id: e.id,
        value: e.value
      });
    }

    setSelectedChoices(newState);
    onChange === null || onChange === void 0 ? void 0 : onChange(newState);
  };

  const handleNAChange = e => {
    const newState = [];

    if (e.checked) {
      newState.push({
        id: e.id,
        value: e.value,
        type: 'NOTA'
      });
    }

    setSelectedChoices(newState);
    onChange === null || onChange === void 0 ? void 0 : onChange(newState);
  };

  const isChecked = id => !!selectedChoices.find(item => item.id === id);

  const {
    imageChoiceContainer,
    imageChoice,
    noneOfTheAboveContainer
  } = useStyles$6();
  return React__default.createElement(QuestionField, {
    id: questionId,
    "data-testid": "ImageChoiceQuestionType",
    errorId: errorId,
    ...questionFieldProps
  }, React__default.createElement("div", {
    className: imageChoiceContainer
  }, choices === null || choices === void 0 ? void 0 : choices.map(choice => {
    return React__default.createElement("div", null, React__default.createElement(ImageChoice$1, {
      key: choice.id,
      id: choice.id,
      disabled: disabled,
      readOnly: readOnly,
      label: choice.label,
      onChange: handleChange,
      className: imageChoice,
      value: choice.id,
      src: choice.image.url,
      alt: choice.image.altText || '',
      name: groupName,
      type: imageChoiceType,
      "aria-describedby": questionFieldProps.error && errorId || undefined,
      checked: isChecked(choice.id)
    }));
  })), choiceNoneOfTheAbove && (choiceNoneOfTheAbove === null || choiceNoneOfTheAbove === void 0 ? void 0 : choiceNoneOfTheAbove.visible) && React__default.createElement("div", {
    className: noneOfTheAboveContainer
  }, React__default.createElement(Checkbox$1, {
    id: choiceNoneOfTheAbove.id,
    value: choiceNoneOfTheAbove.id,
    onChange: handleNAChange,
    checked: isChecked(choiceNoneOfTheAbove.id),
    disabled: disabled,
    readOnly: readOnly,
    "aria-describedby": questionFieldProps.error && errorId || undefined,
    inputSize: INPUT_SIZE
  }, choiceNoneOfTheAbove.label)));
}

var ImageChoiceQuestion$1 = withErrorBoundary(ImageChoiceQuestion);
const useStyles$5 = createUseStyles(() => ({
  rowStyles: {
    display: 'flex',
    flexWrap: 'nowrap',
    alignItems: 'center'
  }
}));

function Row({
  id,
  className,
  children,
  ...props
}) {
  const {
    rowStyles
  } = useStyles$5();
  return React__default.createElement("div", {
    id: id,
    className: classNames(rowStyles, className),
    ...props
  }, children);
}

const useStyles$4 = createUseStyles(theme => ({
  rowStyles: {
    fontSize: 'inherit',
    borderRadius: '3px',
    textAlign: 'center',
    cursor: 'pointer',
    alignItems: 'center',
    ...theme.questionBody
  },
  rowLabel: {
    flex: '1 0 30%',
    padding: [5, 8, 7, 8]
  },
  rowRadio: {
    flex: '1 0 30%',
    padding: [5, 8, 7, 8],
    background: 'none',
    justifyContent: 'center'
  }
}));

function BestWorstRow({
  id,
  label,
  value = null,
  columnIds = [],
  className,
  onChange,
  onClick,
  onKeyDown,
  ...props
}) {
  const [bestId, worstId] = columnIds;
  const bestRadioId = `${id}-${bestId}`;
  const worstRadioId = `${id}-${worstId}`;
  const labelId = `${id}-label`;
  const radioEvents = {
    onClick,
    onKeyDown
  };

  const handleChange = e => {
    onChange === null || onChange === void 0 ? void 0 : onChange({
      id,
      value: e.value,
      checked: e.checked
    });
  };

  const {
    rowStyles,
    rowLabel,
    rowRadio
  } = useStyles$4();
  return React__default.createElement(Row, {
    id: id,
    className: classNames(rowStyles, className),
    ...props
  }, React__default.createElement(Radio$1, {
    id: bestRadioId,
    className: rowRadio,
    value: bestId,
    name: labelId,
    checked: value === bestId,
    "aria-labelledby": `${bestId} ${labelId}`,
    onChange: handleChange,
    ...radioEvents
  }), React__default.createElement("span", {
    className: rowLabel
  }, React__default.createElement(RichText, {
    id: labelId,
    element: "span",
    text: label
  })), React__default.createElement(Radio$1, {
    id: worstRadioId,
    className: classNames(rowRadio),
    value: worstId,
    name: labelId,
    checked: value === worstId,
    "aria-labelledby": `${worstId} ${labelId}`,
    onChange: handleChange,
    ...radioEvents
  }));
}

const useStyles$3 = createUseStyles(theme => ({
  rowList: {
    '& > :nth-child(even)': {
      backgroundColor: `rgba(0,0,0,0.05)`
    }
  },
  header: {
    flex: '1 0 30%',
    ...theme.questionBody,
    padding: '5px 8px 7px 8px',
    textAlign: 'center',
    alignItems: 'center'
  }
}));

function BestWorst({
  id: questionId,
  disabled: _dis,
  readOnly: _ro,
  choices: fields = [],
  responses = [],
  columnChoices = [],
  onChange,
  ...questionFieldProps
}) {
  const [selectedChoices, setSelectedChoices] = useState(responses);
  const [bestAnswerColumn, worstAnswerColumn] = columnChoices;
  const columnIds = columnChoices === null || columnChoices === void 0 ? void 0 : columnChoices.map(c => c.id);
  const errorId = createErrorId(questionId);
  /** determines whether a radio should be rendered as checked or unchecked */

  const getRadioSelection = id => {
    var _a, _b;

    return (_b = (_a = selectedChoices.find(c => c.id === id)) === null || _a === void 0 ? void 0 : _a.value[0].id) !== null && _b !== void 0 ? _b : '';
  };
  /** unChecks selected choice  */


  const unCheckOption = radioId => {
    const newChoices = selectedChoices.filter(c => !radioId.match(c.id));
    setSelectedChoices(newChoices);
    onChange === null || onChange === void 0 ? void 0 : onChange(newChoices);
  };
  /** handles adding new checked selection to responses */


  const handleChange = e => {
    let newChoices = [];
    const rowId = e.id;
    const rowValue = e.value;
    /** remove any other checked radio in the same row or column */

    newChoices = selectedChoices.filter(row => !(row.id === rowId || row.value.find(col => col.value === rowValue)));
    newChoices.push({
      id: rowId,
      value: [{
        id: rowValue,
        value: rowValue
      }]
    });
    setSelectedChoices(newChoices);
    onChange === null || onChange === void 0 ? void 0 : onChange(newChoices);
  };

  const handleClick = e => {
    const input = e.target;
    const isSelected = selectedChoices.find(c => input.id.match(c.id));

    if (isSelected && input.checked) {
      unCheckOption(input.id);
    }
  };

  const handleKeyboard = e => {
    const input = e.target;
    const isSelected = selectedChoices.find(c => input.id.match(c.id));

    if (e.code === 'Space' && isSelected) {
      e.preventDefault();
      unCheckOption(input.id);
    }
  };

  const {
    header,
    rowList
  } = useStyles$3();
  return React__default.createElement(QuestionField, {
    id: questionId,
    "data-testid": "BestWorstQuestionType",
    ...questionFieldProps
  }, React__default.createElement("div", {
    className: rowList
  }, React__default.createElement(Row, null, React__default.createElement("span", {
    id: bestAnswerColumn === null || bestAnswerColumn === void 0 ? void 0 : bestAnswerColumn.id,
    className: classNames(header)
  }, bestAnswerColumn === null || bestAnswerColumn === void 0 ? void 0 : bestAnswerColumn.label), React__default.createElement("span", {
    className: classNames(header)
  }, "\u00A0"), React__default.createElement("span", {
    id: worstAnswerColumn === null || worstAnswerColumn === void 0 ? void 0 : worstAnswerColumn.id,
    className: classNames(header)
  }, worstAnswerColumn === null || worstAnswerColumn === void 0 ? void 0 : worstAnswerColumn.label)), fields === null || fields === void 0 ? void 0 : fields.map(field => {
    return React__default.createElement(BestWorstRow, {
      key: field.id,
      id: field.id,
      label: field.label,
      value: getRadioSelection(field.id),
      columnIds: columnIds,
      onChange: handleChange,
      onClick: handleClick,
      onKeyDown: handleKeyboard,
      "aria-describedby": questionFieldProps.error && errorId || undefined
    });
  })));
}

var BestWorst$1 = withErrorBoundary(BestWorst);
const useStyles$2 = createUseStyles({
  listBox: {
    listStyle: 'none',
    width: 'fit-content',
    overflowY: 'auto',
    overflowX: 'hidden',
    '&:focus-visible': {
      outline: '#005fcc auto 1px',
      outlineOffset: 1
    }
  }
});
/**
 * Scroll to the item at the specified index if it is fully or partially obscured
 * */

const scrollToItem = (itemRect, containerRef) => {
  var _a;

  const ulRect = (_a = containerRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect();

  if (!ulRect || !containerRef.current) {
    return;
  }

  if (itemRect.top < ulRect.top) {
    // eslint-disable-next-line no-param-reassign
    containerRef.current.scrollTop -= ulRect.top - itemRect.top;
  } else if (itemRect.bottom > ulRect.bottom) {
    // eslint-disable-next-line no-param-reassign
    containerRef.current.scrollTop += itemRect.bottom - ulRect.bottom;
  }
};
/**
 * Returns the number of items visible inside the listbox given its current dimensions
 * note: if height is 0, it will return 10 as a default to satisfy tests
 * */


const numItemsVisible = (containerHeight, itemHeight = 48) => {
  return !containerHeight ? 10 : Math.round(containerHeight / itemHeight);
};
/**
 * Focus another item in the listBox depending on which key is pressed
 * @param key The key that was pressed
 * @param focusedIndex The index of the currently focused item
 * @param itemCount The number of items in the listBox
 * @param visibleItemCount The number of items visible in the listBox
 * @param focusItem Function which updates the the currently focused item
 */


const focusNext = (key, focusedIndex, itemCount, visibleItemCount, focusItem) => {
  const lastIndex = itemCount - 1;

  if (key === 'End') {
    focusItem(lastIndex);
    return;
  }

  if (focusedIndex === null) {
    focusItem(0);
    return;
  }

  switch (key) {
    case 'Down':
    case 'ArrowDown':
      if (focusedIndex < lastIndex) {
        focusItem(focusedIndex + 1);
      } else {
        focusItem(0);
      }

      break;

    case 'Up':
    case 'ArrowUp':
      if (focusedIndex > 0) {
        focusItem(focusedIndex - 1);
      } else {
        focusItem(lastIndex);
      }

      break;

    case 'PageDown':
      if (focusedIndex < lastIndex - visibleItemCount) {
        focusItem(focusedIndex + visibleItemCount);
      } else {
        focusItem(lastIndex);
      }

      break;

    case 'PageUp':
      if (focusedIndex > visibleItemCount) {
        focusItem(focusedIndex - visibleItemCount);
      } else {
        focusItem(0);
      }

      break;

    case 'Home':
      focusItem(0);
      break;
  }
};

React__default.forwardRef(({
  children,
  focusedIndex: externalFocusedIndex,
  tabIndex,
  onKeyDown,
  onClick,
  onChange,
  className,
  ...rest
}, ref) => {
  const {
    listBox
  } = useStyles$2();
  const [focusedIndex, setFocusedIndex] = useState(null); // override internal focusedIndex with external state if provided

  useEffect(() => {
    if (externalFocusedIndex !== undefined) {
      setFocusedIndex(externalFocusedIndex);
    }
  }, [externalFocusedIndex]);
  const itemsRef = useRef([]);
  const ulRef = useRef(null);

  const focusItem = index => {
    setFocusedIndex(index);
  };

  useImperativeHandle(ref, () => ulRef.current);
  useEffect(() => {
    var _a, _b;

    if (focusedIndex === null) {
      return;
    } // Scroll to the currently focused item anytime the focused index changes


    scrollToItem((_a = itemsRef.current) === null || _a === void 0 ? void 0 : _a[focusedIndex].getBoundingClientRect(), ulRef); // call the onChange callback if the focused index changes

    onChange === null || onChange === void 0 ? void 0 : onChange({
      target: itemsRef.current[focusedIndex],
      value: (_b = itemsRef.current[focusedIndex].dataset.value) !== null && _b !== void 0 ? _b : ''
    });
  }, [focusedIndex]);

  const handleKeyDown = e => {
    var _a;

    onKeyDown === null || onKeyDown === void 0 ? void 0 : onKeyDown(e);

    if (!(e.target instanceof HTMLUListElement)) {
      return;
    }

    if (['Down', 'Up', 'ArrowDown', 'ArrowUp', 'PageDown', 'PageUp', 'End', 'Home'].includes(e.key)) {
      e.preventDefault(); // Focus another item in the listBox depending on which key is pressed

      focusNext(e.key, focusedIndex, itemsRef.current.length, numItemsVisible((_a = ulRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect().height), focusItem);
    }
  };

  const handleClick = e => {
    onClick === null || onClick === void 0 ? void 0 : onClick(e);

    if (e.target instanceof HTMLLIElement) {
      setFocusedIndex(itemsRef.current.indexOf(e.target));
    }
  };

  const interactiveProps = {
    tabIndex,
    'aria-activedescendant': tabIndex !== undefined && focusedIndex !== null ? `ListBoxItem-${focusedIndex}` : undefined
  };
  return React__default.createElement("ul", {
    ref: ulRef,
    className: classNames(listBox, className),
    role: "listbox",
    onKeyDown: handleKeyDown,
    onClick: handleClick,
    ...interactiveProps,
    ...rest
  }, React__default.Children.map(children, (child, index) => {
    var _a;

    return React__default.isValidElement(child) && React__default.cloneElement(child, {
      key: child.props.value,
      id: (_a = child.props.id) !== null && _a !== void 0 ? _a : `ListBoxItem-${index}`,
      selected: index === focusedIndex,
      ref: el => {
        itemsRef.current[index] = el;
      }
    });
  }));
});
const useStyles$1 = createUseStyles({
  listBoxItem: ({
    selected,
    disabled
  }) => ({
    display: 'flex',
    alignItems: 'center',
    maxWidth: '100%',
    backgroundColor: '#FFF',
    cursor: 'pointer',
    ...(selected && {
      backgroundColor: 'rgb(237, 238, 238)'
    }),
    '&:hover, &:focus': {
      backgroundColor: 'rgb(237, 238, 238)'
    },
    '&:active': {
      backgroundColor: 'rgb(224, 226, 226)'
    },
    ...(disabled && {
      opacity: 0.5,
      pointerEvents: 'none'
    })
  })
});
React__default.forwardRef(({
  children,
  disabled,
  selected,
  value,
  className,
  ...rest
}, ref) => {
  const {
    listBoxItem
  } = useStyles$1({
    disabled,
    selected
  });
  return React__default.createElement("li", {
    ref: ref,
    "data-value": value,
    className: classNames(listBoxItem, className),
    role: "option",
    "aria-selected": selected ? true : undefined,
    ...rest
  }, children);
});
const useStyles = createUseStyles({
  listBoxContainer: {
    // TODO: Possible to utilize focus-visible to show only on keyboard interaction? (with child)
    '&:focus-within': {
      outline: '#005fcc auto 1px',
      outlineOffset: 1
    }
  },
  listBox: {
    listStyle: 'none',
    width: 'fit-content',
    overflowY: 'auto',
    overflowX: 'hidden',
    padding: 0
  }
});
const ARIA_ACTIVEDESCENDANT = 'aria-activedescendant';
/**
 * A virtualized version of the List component, using `react-window` to render large datasets. This component should only be used when the number of items would cause performance issues with the standard List component (most likely >500 items).
 */

React__default.forwardRef(({
  height = 325,
  width = 175,
  itemSize,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  [ARIA_ACTIVEDESCENDANT]: ariaActiveDescendant,
  tabIndex,
  itemCount,
  focusedIndex: externalFocusedIndex,
  onChange,
  onKeyDown,
  onClick,
  className,
  children,
  ...rest
}, ref) => {
  const [focusedIndex, setFocusedIndex] = useState(null);
  const listRef = useRef(null);
  const innerRef = useRef(null);
  const outerRef = useRef(null);
  const itemsRef = useRef([]); // override internal focusedIndex with external state if provided

  useEffect(() => {
    var _a;

    if (externalFocusedIndex !== undefined) {
      if (externalFocusedIndex !== null) {
        (_a = listRef.current) === null || _a === void 0 ? void 0 : _a.scrollToItem(externalFocusedIndex);
      }

      setFocusedIndex(externalFocusedIndex);
    }
  }, [externalFocusedIndex]);
  const {
    listBoxContainer,
    listBox
  } = useStyles();

  const handleChange = item => {
    var _a;

    onChange === null || onChange === void 0 ? void 0 : onChange({
      target: item,
      value: (_a = item.dataset.value) !== null && _a !== void 0 ? _a : ''
    });
  };
  /**
   * Scroll to and focus the item at the specified index
   */


  const focusItem = index => {
    var _a;

    (_a = listRef.current) === null || _a === void 0 ? void 0 : _a.scrollToItem(index);
    setFocusedIndex(index);
    handleChange(itemsRef.current[index]);
  };

  useImperativeHandle(ref, () => ({
    listRef,
    innerRef,
    outerRef,
    itemsRef
  }));
  const handleKeyDown = useCallback(e => {
    var _a;

    onKeyDown === null || onKeyDown === void 0 ? void 0 : onKeyDown(e);

    if (!(e.target instanceof HTMLUListElement)) {
      return;
    }

    if (['Down', 'Up', 'ArrowDown', 'ArrowUp', 'PageDown', 'PageUp', 'End', 'Home'].includes(e.key)) {
      e.preventDefault(); // Focus another item in the listBox depending on which key is pressed

      focusNext(e.key, focusedIndex, itemCount, numItemsVisible((_a = outerRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect().height), focusItem);
    }
  }, [focusedIndex, itemCount, onKeyDown]);
  const handleClick = useCallback(e => {
    onClick === null || onClick === void 0 ? void 0 : onClick(e);

    if (e.target instanceof HTMLLIElement) {
      setFocusedIndex(itemsRef.current.indexOf(e.target));
      handleChange(e.target);
    }
  }, [onClick]);
  useLayoutEffect(() => {
    var _a;

    const listElement = innerRef.current;
    (_a = outerRef.current) === null || _a === void 0 ? void 0 : _a.removeAttribute('tabindex');

    if (listElement) {
      listElement.className = listBox;
      listElement.setAttribute('role', 'listbox');
      listElement.addEventListener('keydown', handleKeyDown);
      listElement.addEventListener('click', handleClick);

      if (tabIndex === undefined) {
        listElement.removeAttribute('tabindex');
      } else {
        listElement.setAttribute('tabindex', tabIndex === null || tabIndex === void 0 ? void 0 : tabIndex.toString());
      }

      if (ariaLabel) {
        listElement.setAttribute('aria-label', ariaLabel);
      }

      if (ariaLabelledBy) {
        listElement.setAttribute('aria-labelledby', ariaLabelledBy);
      }
    }

    return () => {
      if (listElement) {
        listElement.removeEventListener('keydown', handleKeyDown);
        listElement.removeEventListener('click', handleClick);
      }
    };
  }, [ariaLabel, ariaLabelledBy, handleClick, handleKeyDown, listBox, tabIndex]);
  useLayoutEffect(() => {
    const listElement = innerRef.current;

    if (ariaActiveDescendant || tabIndex !== undefined && focusedIndex !== null) {
      listElement === null || listElement === void 0 ? void 0 : listElement.setAttribute(ARIA_ACTIVEDESCENDANT, ariaActiveDescendant !== null && ariaActiveDescendant !== void 0 ? ariaActiveDescendant : `ListBoxItem-${focusedIndex}`);
    } else {
      listElement === null || listElement === void 0 ? void 0 : listElement.removeAttribute(ARIA_ACTIVEDESCENDANT);
    }
  }, [ariaActiveDescendant, focusedIndex, tabIndex]);
  return React__default.createElement(FixedSizeList, {
    className: classNames(listBoxContainer, className),
    itemSize: itemSize,
    height: Math.min(height, itemCount * itemSize),
    width: width,
    innerElementType: "ul",
    innerRef: innerRef,
    outerRef: outerRef,
    overscanCount: 10,
    itemCount: itemCount,
    ref: listRef,
    ...rest
  }, props => {
    var _a;

    return React__default.cloneElement(children(props), {
      id: (_a = children(props).props.id) !== null && _a !== void 0 ? _a : `ListBoxItem-${props.index}`,
      selected: props.index === focusedIndex,
      ref: el => {
        itemsRef.current[props.index] = el;
      }
    });
  });
});
export { BestWorst$1 as BestWorst, CaretDownIcon, CaretDownOutlineIcon, CheckboxQuestion$1 as CheckboxQuestion, CommentBox$1 as CommentBox, Dropdown$1 as Dropdown, ImageChoiceQuestion$1 as ImageChoiceQuestion, ImagePresentational$1 as ImagePresentational, MultipleChoice, NativeSelect, SelectAnswerOption as NativeSelectOption, SingleTextbox$1 as SingleTextbox, TextPresentational$1 as TextPresentational };